import React, { useState, useMemo, useCallback } from "react";
import {
  calcSD as _calcSD,
  calcLCT as _calcLCT,
  isEvFbtExempt,
  incomeTax,
  medicareLevy,
  getResidualByTermAndKm,
  LCT_THRESHOLD_STD,
  LCT_THRESHOLD_FE,
  TAX_BRACKETS,
  FBT_STATUTORY,
  GST_PURCHASE_CREDIT_CAP,
  ONCOSTS_META,
  SCENARIO_SCHEMA_VERSION,
  CHANGES,
  RATE_BENCHMARK,
} from './veercal.oncosts';
import { tokensV2 } from './design/v2/tokens';
import { Level, Meter } from './design/v2/Gauges';
import { equityAtExit, rateCheck } from './design/v2/meterRules';
import { SiteHeader, SiteFooter } from './design/v2/SiteChrome';
import DepthControl from './results/DepthControl';
import BentoResults from './results/BentoResults';
import { buildCostComparison } from './results/breakdown';

/**
 * Veercal — Car Ownership Cost Calculator
 * © 2025 Veercal. All rights reserved.
 * https://www.veercal.com
 *
 * This source code is proprietary and confidential.
 * Unauthorised copying, modification, distribution, or use of this
 * software, in whole or in part, without the prior written consent
 * of Veercal is strictly prohibited.
 *
 * General information only — not financial advice.
 */
import { LineChart, Line, AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList, Customized } from "recharts";

/* ─── Styles ─────────────────────────────────────────────────────────────────
   Design system v2 ("Tell-tale"): fonts, tokens and every vc- class come from
   /design/v2/fonts.css, tokens.css and site.css, linked in public/index.html
   (self-hosted Barlow, nothing from Google). Layout, the phone inputs drawer
   and the print rules are in site.css, "React calculator and Quick Compare". */

/* ─── Meta tags ──────────────────────────────────────────────────────────── */
const setMeta = (name, content, prop = false) => {
  const el = document.createElement("meta");
  el.setAttribute(prop ? "property" : "name", name);
  el.setAttribute("content", content);
  document.head.appendChild(el);
};
setMeta("author",           "Veercal");
setMeta("copyright",        `© ${new Date().getFullYear()} Veercal. All rights reserved.`);
setMeta("description",      "Australian car ownership cost calculator — compare cash, personal loan, dealer finance, finance lease, and novated lease side by side. True total cost including depreciation, stamp duty, FBT, and running costs.");
setMeta("og:title",         "Veercal — Car Ownership Cost Calculator", true);
setMeta("og:description",   "Compare every car finance structure in Australia. True cost analysis including FBT, stamp duty, depreciation, and running costs.", true);
setMeta("og:type",          "website", true);
setMeta("og:image",         "https://www.veercal.com/og-image.png", true);  /* TODO: create and upload 1200x630 branded image */
setMeta("og:url",           "https://www.veercal.com", true);
setMeta("og:site_name",     "Veercal", true);
setMeta("twitter:card",     "summary_large_image");
setMeta("twitter:title",    "Veercal — Car Ownership Cost Calculator");
setMeta("twitter:description", "Compare cash, loans, dealer finance, leases, and novated leases. True cost analysis for Australian car buyers.");

/* Set page title */
document.title = "Veercal — Car Ownership Cost Calculator | Australia";

/* FAQ structured data for rich results */
if (!document.getElementById("veercal-faq-schema")) {
  const faqSchema = document.createElement("script");
  faqSchema.id = "veercal-faq-schema";
  faqSchema.type = "application/ld+json";
  faqSchema.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What is a novated lease?",
        "acceptedAnswer": { "@type": "Answer", "text": "A novated lease is a three-way arrangement between you, your employer, and a finance company. Lease payments are deducted from your pre-tax salary, reducing your taxable income. It is only available to PAYG employees. General information only — not financial advice." }},
      { "@type": "Question", "name": "How is stamp duty calculated on a car in Australia?",
        "acceptedAnswer": { "@type": "Answer", "text": "Stamp duty on motor vehicles is a state government charge calculated on the vehicle's market value. Rates vary significantly by state — from around 3% in QLD and TAS to 6% in VIC for vehicles over $100,000. General information only." }},
      { "@type": "Question", "name": "What is the true cost of owning a car?",
        "acceptedAnswer": { "@type": "Answer", "text": "The true cost includes purchase price, stamp duty, finance interest, depreciation, fuel, insurance, registration, servicing, and tyres — minus the exit sale value. The Veercal calculator models all of these together for accurate comparison across finance structures." }},
      { "@type": "Question", "name": "Is a novated lease better than a personal loan?",
        "acceptedAnswer": { "@type": "Answer", "text": "It depends on your salary, vehicle price, and how much you drive. For employees earning $80,000+ a novated lease typically shows a lower after-tax cost, especially for eligible EVs which are FBT-exempt. Use the Veercal calculator to compare your specific scenario. General information only — not financial advice." }}
    ]
  });
  document.head.appendChild(faqSchema);
}

/* ─── Methods ────────────────────────────────────────────────────────────────
   Labels and icon keys. No colours: in v2 the option being discussed is amber
   and every other option ink (docs/brand/DESIGN.md §5), and no icons are drawn
   (DESIGN.md §10; Pat, 22 Oct 2026). */
const METHOD_META = {
  cash:    { label: "Cash Purchase",    icon: "cash" },
  loan:    { label: "Personal Loan",    icon: "loan" },
  dealer:  { label: "Dealer Finance",   icon: "dealer" },
  lease:   { label: "Finance Lease",    icon: "lease" },
  novated: { label: "Novated Lease",    icon: "novated" },
};

/* The pinned engine region below reads tokens.font.body and tokens.muted (NUM and
   LBL). This stand-in gives them the v2 values, so nothing here imports the v1
   tokens (overhaul Phase 4; Pat, 22 Oct 2026). Everything else uses tokensV2 or
   the vc- classes. */
const tokens = { font: { body: tokensV2.font }, muted: tokensV2.textSecondary };

export const DEFAULTS = {
  vehiclePrice: 45000, holdYears: 5, annualKm: 15000,
  opportunityCostPct: 0.05,
  loanRate: 0.0899, loanTermYears: 5, loanDeposit: 5000,
  dealerRate: 0.0699, dealerTermYears: 5, dealerDeposit: 2000, balloonPct: 0.25,
  leaseRate: 0.065, leaseResidualPct: 0.5288, leaseTermYears: 3,
  grossSalary: 100000, novatedRate: 0.065, novatedTermYears: 3, novatedResidualPct: 0.5288, novatedProviderFeeAnnual: 500,
  fuelPerL: 2.10, fuelL100km: 9, tyresPerYear: 600,
  servicesPerYear: 800, insurancePerYear: 1800, regPerYear: 900,
  depYr1: 0.15, depYr2: 0.12, depYrN: 0.10,
  isEV: false, evType: 'bev', evHomeChargeKwh: 0.28, evEfficiencyKwh: 18, evGovtRebate: 0,
  evPctHomeCharge: 0.80, evPublicChargeKwh: 0.60,
  state: "VIC",
  refinanceYear: 0, refinanceRate: 0.059,
  financeStampDuty: false,
  novatedGstOnPurchase: true,
};

/* ─── RATES CONFIG ──────────────────────────────────────────────────────────
   Every government-set rate (FBT, tax brackets, Medicare, LCT thresholds,
   stamp duty schedules, statutory residuals, EV concessions) lives in
   src/veercal.oncosts.js — the single source of truth. Imports at the top
   of this file pull in what's needed; do not redeclare anything here.
─────────────────────────────────────────────────────────────────────────── */



/* ─── Stamp duty ─────────────────────────────────────────────────────────── */
/* ─── On-costs: stamp duty and LCT ──────────────────────────────────────────
   Imported from the single source of truth — veercal.oncosts.js.
   All rates verified against state revenue offices, October 2026.
   DO NOT edit these formulas here — edit veercal.oncosts.js instead,
   then also update the inline copies in stamp-duty.html and novated.html.
─────────────────────────────────────────────────────────────────────────── */

/* Wrap with the signature the rest of App.js expects.
   vType maps the calculator's evType ('bev' | 'phev') to oncosts.calcSD's
   vehicle-type domain ('ev' | 'phev' | 'new'). EV concessions (VIC green
   flat rate, QLD hybrid rate, ACT zero-emission rate, NT first $50k) only apply
   when this third argument is set correctly. */
export const sdVType = (d) =>
  d.isEV ? (d.evType === 'phev' ? 'phev' : 'ev') : 'new';
export const calcSD  = (p, st, vType = 'new') => _calcSD(p, st, vType, false);
export const calcLCT = (p, isEV) => _calcLCT(p, isEV);

/* Legacy constants still referenced in the UI layer */
const LCT_STD = LCT_THRESHOLD_STD;  /* 80809 (FY2026-27) */
const LCT_FE  = LCT_THRESHOLD_FE;   /* 91661 (FY2026-27) */

/* ─── Finance maths ─────────────────────────────────────────────────────── */
export function pmt(rate, nper, pv, fv = 0) {
  const r = rate / 12;
  if (r === 0) return (pv + fv) / nper;
  return (r * (pv * Math.pow(1 + r, nper) + fv)) / (Math.pow(1 + r, nper) - 1);
}
export function buildAmort(principal, rate, months, balloon = 0) {
  const r = rate / 12, pay = pmt(rate, months, principal, -balloon);
  let bal = principal, tot = 0;
  const rows = [];
  for (let m = 1; m <= months; m++) {
    const int = bal * r, pp = pay - int;
    bal = bal - pp; tot += int;
    rows.push({ month: m, payment: pay, interest: int, principalPaid: pp, balance: Math.max(0, bal), totalInterest: tot });
  }
  return { rows, totalInterest: tot, monthlyPayment: pay };
}
export function vehVal(price, yr, d) {
  let v = price;
  for (let y = 1; y <= yr; y++) v *= 1 - (y === 1 ? d.depYr1 : y === 2 ? d.depYr2 : d.depYrN);
  return Math.max(v, price * 0.05);
}
/* Marginal tax rate at a given salary — derived from TAX_BRACKETS so the
   UI label stays in sync with the calc the moment brackets are updated. */
export function mtr(sal) {
  const b = TAX_BRACKETS.slice().reverse().find(b => sal >= b.min);
  return b ? b.rate : 0;
}
/* Effective Medicare levy rate for the UI ("Medicare levy 1.04% / 2.00%").
   In the $28,011–$35,013 phase-in band the effective rate is < 2%; above the
   upper threshold it's the full 2%. Derived from medicareLevy() so the
   displayed rate matches the dollar amount used in tax calculations. */
export function med(sal) { return sal > 0 ? medicareLevy(sal) / sal : 0; }

/* ATO statutory residual by lease term + annual km — wraps the shared
   getResidualByTermAndKm() in oncosts.js. Scales the 36-month km table by
   the ATO IT 28 term factor so 5-year leases get a lower residual floor.
   3-year leases are unchanged from the prior km-only table. */
export function atoResidual(annualKm, termYears = 3) {
  return getResidualByTermAndKm(termYears, annualKm);
}
export function running(d) {
  if (d.isEV) {
    /* Charging cost split between home (cheap, slow) and public DC fast (more
       expensive). Typical AU profile is ~80% home, 20% public DC at $0.50-0.75/kWh.
       Both percentages are user-tweakable; defaults to evPctHomeCharge (0.80)
       and evPublicChargeKwh ($0.60). Older saved scenarios without these fields
       fall back to 100% home charging (the prior behaviour). */
    const totalKwh = (d.annualKm / 100) * d.evEfficiencyKwh;
    const homePct = d.evPctHomeCharge != null ? d.evPctHomeCharge : 1;
    const publicRate = d.evPublicChargeKwh != null ? d.evPublicChargeKwh : d.evHomeChargeKwh;
    const chargeCost = totalKwh * homePct * d.evHomeChargeKwh
                     + totalKwh * (1 - homePct) * publicRate;
    return chargeCost + d.tyresPerYear + d.servicesPerYear * 0.6 + d.insurancePerYear + d.regPerYear;
  }
  return (d.annualKm / 100) * d.fuelL100km * d.fuelPerL + d.tyresPerYear + d.servicesPerYear + d.insurancePerYear + d.regPerYear;
}
/* ─── Drive-away → base price solver ────────────────────────────────────────
   Drive-away prices (as advertised) include BOTH stamp duty AND Luxury Car Tax.
   LCT is charged by the manufacturer and baked into the dealer drive-away price.
   We solve: driveAway = basePrice + stampDuty(basePrice) + LCT(basePrice)
   Matches stamp-duty.html and QuickCompare.jsx.
─────────────────────────────────────────────────────────────────────────── */
export function baseFromDriveAway(driveAway, state, isFuelEfficient = false, vType = 'new') {
  /* SD is levied on the dutiable value which includes LCT (LCT is part of the
     purchase price). vType lets calcSD apply the state EV concessions (VIC,
     QLD, ACT, NT). Typically converges in
     3-5 iterations; we cap at 20 as a safety net. */
  let base = driveAway * 0.88; /* initial estimate — leaves room for ~12% SD+LCT */
  for (let i = 0; i < 20; i++) {
    const next = driveAway - calcLCT(base, isFuelEfficient) - calcSD(base + calcLCT(base, isFuelEfficient), state, vType);
    if (Math.abs(next - base) < 0.5) { base = next; break; }
    base = next;
  }
  return Math.round(base);
}

/* Helper: stamp duty on dutiable value (base price + LCT) */
export function stampDutyForDisplay(d) {
  const vType = sdVType(d);
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, vType);
  return calcSD(base + calcLCT(base, d.isEV), d.state, vType);
}

export function effPrice(d) {
  /* Drive-away includes BOTH stamp duty AND LCT (LCT is baked into dealer prices).
     Nothing is added on top — just deduct any government EV rebate. */
  return d.vehiclePrice - (d.isEV ? d.evGovtRebate : 0);
}
/* ─── Calculators ────────────────────────────────────────────────────────── */
export function calcCash(d) {
  const ep = effPrice(d), run = running(d), yrs = d.holdYears;
  const yd = [];
  for (let y = 1; y <= yrs; y++) {
    const av = vehVal(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), y, d);
    /* Compound opportunity cost: what ep would have grown to if invested.
       Include it in cumulative totalSpent so the year-by-year chart matches
       the headline total (which already accrues oppCostTotal). */
    const oppCost = ep * (Math.pow(1 + d.opportunityCostPct, y) - 1);
    yd.push({ year: y, assetValue: Math.round(av), equity: Math.round(av),
      totalSpent: Math.round(ep + run * y + oppCost),
      annualSpent: y === 1 ? Math.round(ep + run) : Math.round(run),  /* upfront in yr1, running only after */
      monthlyEquivalent: Math.round(ep / (yrs * 12) + run / 12),
      netPosition: Math.round(av - ep - run * y - oppCost),
      opportunityCost: Math.round(oppCost) });
  }
  const ev = vehVal(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), yrs, d);
  /* True cost includes compounded opportunity cost of deploying capital */
  const oppCostTotal = ep * (Math.pow(1 + d.opportunityCostPct, yrs) - 1);
  const tc = ep + run * yrs - ev + oppCostTotal;
  return { method: "cash", yearlyData: yd, totalCost: Math.round(tc), upfrontCost: Math.round(ep),
    monthlyPayment: 0, exitValue: Math.round(ev), totalInterest: 0,
    opportunityCostTotal: Math.round(oppCostTotal),
    effectiveMonthly: Math.round(tc / (yrs * 12)),
    lct: Math.round(calcLCT(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), d.isEV)),
    stampDuty: Math.round(stampDutyForDisplay(d)), onCosts: 0 };
}

export function calcLoan(d, type) {
  const isD = type === "dealer";
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)); /* base price: drive-away minus SD and LCT */
  const rate = isD ? d.dealerRate : d.loanRate;
  const termYrs = isD ? d.dealerTermYears : d.loanTermYears;
  const dep = isD ? d.dealerDeposit : d.loanDeposit;
  const balloon = isD ? base * d.balloonPct : 0;
  const sd = stampDutyForDisplay(d);           /* stamp duty: upfront unless financed */
  /* Finance base + LCT. Stamp duty is upfront by default; if d.financeStampDuty
     is set, it rolls into the loan principal (matches what most personal loan
     applications and dealer finance contracts will allow). */
  const financedSd = d.financeStampDuty ? sd : 0;
  const upfrontSd  = d.financeStampDuty ? 0 : sd;
  const financedAmount = base + calcLCT(base, d.isEV) + financedSd - dep;
  const principal = Math.max(0, financedAmount), months = termYrs * 12;
  let amort = buildAmort(principal, rate, months, balloon);
  let monthlyPaymentRefi = null;   /* M3: post-refi payment, null if no refi */
  let refinanceMonth = null;
  if (d.refinanceYear > 0 && d.refinanceYear < termYrs) {
    refinanceMonth = d.refinanceYear * 12;
    const balRf = amort.rows[refinanceMonth - 1].balance;
    const a2 = buildAmort(balRf, d.refinanceRate, months - refinanceMonth, balloon);
    monthlyPaymentRefi = a2.monthlyPayment;
    amort = { ...amort, totalInterest: amort.rows[refinanceMonth - 1].totalInterest + a2.totalInterest };
  }
  const { rows, totalInterest, monthlyPayment } = amort;
  const run = running(d), yrs = d.holdYears;
  /* Opportunity cost on the upfront cash outlay (deposit + upfront SD) so the
     comparison vs cash is symmetric — cash deploys the full purchase price and
     incurs opp cost; financed structures deploy the upfront only. If SD is
     financed (M12), the SD share isn't deployed upfront so opp cost shrinks. */
  const upfront = dep + upfrontSd;
  /* Cumulative payments helper — accounts for refinance switching the payment
     amount mid-loan. Pre-refi months use monthlyPayment; post-refi use the
     re-amortised monthlyPaymentRefi. */
  const cumulativePayments = (mElapsed) => {
    const m = Math.min(mElapsed, months);
    if (monthlyPaymentRefi == null || m <= refinanceMonth) {
      return m * monthlyPayment;
    }
    return refinanceMonth * monthlyPayment + (m - refinanceMonth) * monthlyPaymentRefi;
  };
  const yd = [];
  for (let y = 1; y <= yrs; y++) {
    const av = vehVal(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), y, d);
    const mIdx = Math.min(y * 12, months) - 1;
    const lb = y * 12 <= months ? rows[mIdx].balance : (isD ? balloon : 0);
    const tp = dep + cumulativePayments(y * 12);
    const prevTp = dep + cumulativePayments((y - 1) * 12);
    const oppCostY = upfront * (Math.pow(1 + d.opportunityCostPct, y) - 1);
    yd.push({ year: y, assetValue: Math.round(av), loanBalance: Math.round(lb),
      equity: Math.round(av - lb),
      totalSpent: Math.round(tp + run * y + oppCostY),
      annualSpent: Math.round((tp - prevTp) + run),  /* payments this year + annual running */
      monthlyEquivalent: Math.round(monthlyPayment + run / 12),
      netPosition: Math.round(av - lb - run * y - oppCostY),
      opportunityCost: Math.round(oppCostY) });
  }
  const ev = vehVal(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), yrs, d);
  const lb = yrs * 12 <= months ? rows[Math.min(yrs * 12, months) - 1].balance : (isD ? balloon : 0);
  const tp = dep + cumulativePayments(yrs * 12);
  const oppCostTotal = upfront * (Math.pow(1 + d.opportunityCostPct, yrs) - 1);
  /* When SD is financed, it's already in `tp` (via principal); only add upfront SD here. */
  const tc = tp + upfrontSd + run * yrs - ev + Math.max(0, lb) + oppCostTotal;
  return { method: type, yearlyData: yd, totalCost: Math.round(tc), upfrontCost: Math.round(dep + upfrontSd),
    monthlyPayment: Math.round(monthlyPayment),
    monthlyPaymentRefi: monthlyPaymentRefi != null ? Math.round(monthlyPaymentRefi) : null,
    refinanceYear: d.refinanceYear > 0 ? d.refinanceYear : null,
    exitValue: Math.round(ev),
    totalInterest: Math.round(totalInterest), effectiveMonthly: Math.round(tc / (yrs * 12)),
    balloon: Math.round(balloon), loanBalance: Math.round(lb),
    opportunityCostTotal: Math.round(oppCostTotal),
    lct: Math.round(calcLCT(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), d.isEV)),
    stampDuty: Math.round(stampDutyForDisplay(d)), onCosts: 0 };
}

export function calcLease(d) {
  /* Residual: ATO statutory % applied to drive-away price (the full cost to
     the financier). C5 fix: running costs scoped to the lease term, not the
     full hold period — once the lease ends and the user returns the car they
     have no vehicle and no running costs (unlike a novated lease where they
     buy out the residual). Costs after the lease end depend on what they do
     next; use /end-of-term to model the handback / re-lease decision. */
  const months = d.leaseTermYears * 12, res = d.vehiclePrice * d.leaseResidualPct;
  const { monthlyPayment, totalInterest } = buildAmort(d.vehiclePrice, d.leaseRate, months, res);
  const run = running(d), yrs = d.holdYears;
  const runYears = Math.min(yrs, d.leaseTermYears);
  const yd = [];
  for (let y = 1; y <= yrs; y++) {
    const lp = Math.min(y, d.leaseTermYears) * 12 * monthlyPayment;
    const runYearsAtY = Math.min(y, d.leaseTermYears);
    yd.push({ year: y, assetValue: 0, equity: 0,
      totalSpent: Math.round(lp + run * runYearsAtY),
      annualSpent: Math.round(Math.min(12, Math.max(0, d.leaseTermYears*12 - (y-1)*12)) * monthlyPayment + (y <= d.leaseTermYears ? run : 0)),
      monthlyEquivalent: Math.round(monthlyPayment + run / 12),
      netPosition: Math.round(-(lp + run * runYearsAtY)) });
  }
  const tl = Math.min(yrs, d.leaseTermYears) * 12 * monthlyPayment;
  const totalRunning = run * runYears;
  return { method: "lease", yearlyData: yd, totalCost: Math.round(tl + totalRunning),
    upfrontCost: 0, monthlyPayment: Math.round(monthlyPayment),
    exitValue: 0, residualBuyout: Math.round(res), totalInterest: Math.round(totalInterest),
    effectiveMonthly: Math.round((tl + totalRunning) / (yrs * 12)),
    leaseTermYears: d.leaseTermYears,   /* expose so the UI can show the "ends at year N" note */
    onCosts: 0, lct: 0, stampDuty: 0 };
}

export function calcNovated(d) {
  const base = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d));
  /* FBT exemption test — centralised in oncosts.js so the answer here matches
     /compare and /novated for the same scenario. The function extracts base
     internally and returns true iff no LCT would have been payable. */
  const evExempt = isEvFbtExempt(d.vehiclePrice, sdVType(d), d.state);
  const leaseMos = d.novatedTermYears * 12;
  /* ATO "cost price" used for FBT statutory base and residual minimum:
     GST-inclusive purchase price including dealer delivery and LCT, but
     excluding registration and CTP (per TD 2017/16, FBT guidance). In
     Veercal's model: base price from the drive-away solver + LCT.
     (Stamp duty treatment is debated industry-wide; we exclude it here to
     stay aligned with the leasing-provider convention.) */
  const lct = calcLCT(base, d.isEV);
  const fbtCostPrice = base + lct;
  const res = fbtCostPrice * d.novatedResidualPct;
  /* GST input credit on the vehicle purchase: the financier buys the car, claims the
     GST credit (capped at 1/11 of the ATO car limit), and funds the lease on the
     GST-exclusive price. base is GST-inclusive ex-LCT/SD/rego — its GST portion is
     base/11. Toggle off (novatedGstOnPurchase=false) reverts to financing GST-inclusive. */
  const purchaseGstCredit = d.novatedGstOnPurchase
    ? Math.min(base / 11, GST_PURCHASE_CREDIT_CAP)
    : 0;
  /* Principal: drive-away price (SD + LCT financed) less any purchase GST credit */
  const principal = d.vehiclePrice - purchaseGstCredit;
  const { monthlyPayment: gf } = buildAmort(principal, d.novatedRate, leaseMos, res);
  const runMo = running(d) / 12;
  const providerFeeMo = (d.novatedProviderFeeAnnual || 0) / 12;
  /* Provider fee is packaged pre-tax in a novated lease (ATO allows running costs including
     management fees to be salary sacrificed). Include in sacrifice, not post-tax. */
  const preTax = gf + runMo + providerFeeMo;   /* full salary sacrifice amount/mo */
  /* Tax saving: bracket method (accurate for all salaries — handles bracket crossing).
     Flat marginal rate overstates savings when sacrifice crosses bracket boundaries,
     e.g. $200k salary where sacrifice reduces income from 45% into the 37% bracket.
     Uses incomeTax() + Medicare from oncosts.js — single source of truth. */
  const annualSacrifice = preTax * 12;
  const totalTax = (s) => incomeTax(s) + medicareLevy(s);
  const taxBefore = totalTax(d.grossSalary);
  const taxAfter  = totalTax(Math.max(0, d.grossSalary - annualSacrifice));
  const saving = (taxBefore - taxAfter) / 12;  /* monthly income tax saving */
  const net = preTax - saving;                 /* after-tax cost of sacrifice */
  /* FBT: Employee Contribution Method (ECM) — employee's post-tax contribution equals
     the taxable value (FBT cost price × statutory rate), which reduces employer FBT
     liability to zero. ECM = fbtCostPrice × FBT_STATUTORY / 12. The cost price
     includes LCT per ATO TD 2017/16. */
  const fbtMo = evExempt ? 0 : (fbtCostPrice * FBT_STATUTORY) / 12;
  /* GST input credit: providers claim back the GST on packaged running costs and
     credit it to the employee. Applies to GST-inclusive items (fuel/charging,
     insurance, servicing, tyres, provider fee) — NOT registration (no GST in AU).
     1/11th of the GST-inclusive amount is the GST portion. */
  const gstApplicableAnnual = Math.max(0, running(d) - d.regPerYear) + (d.novatedProviderFeeAnnual || 0);
  const gstCreditMo = gstApplicableAnnual / 11 / 12;
  const trueNet = net + fbtMo - gstCreditMo; /* true monthly out-of-pocket during lease */

  const yrs = d.holdYears, leaseTerm = d.novatedTermYears;
  const runPerYear = running(d);
  const yd = [];

  /* Cumulative cost tracker */
  let cumCost = 0;
  for (let y = 1; y <= yrs; y++) {
    const av = vehVal(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), y, d);
    if (y <= leaseTerm) {
      /* Phase 1: lease active — salary sacrifice covers finance + running */
      const annualCost = trueNet * 12;
      cumCost += annualCost;
      /* Add residual in the final lease year */
      if (y === leaseTerm) cumCost += res;
      yd.push({ year: y, assetValue: Math.round(av), equity: Math.round(av - res),
        totalSpent: Math.round(cumCost),
        annualSpent: Math.round(y === leaseTerm ? annualCost + res : annualCost),
        monthlyEquivalent: Math.round(trueNet),
        taxSavingAnnual: Math.round(saving * 12),
        netPosition: Math.round(av - cumCost),
        residualBuyout: Math.round(res) });
    } else {
      /* Phase 2: post-lease — own the car, only running costs */
      cumCost += runPerYear;
      yd.push({ year: y, assetValue: Math.round(av), equity: Math.round(av),
        totalSpent: Math.round(cumCost),
        annualSpent: Math.round(runPerYear),
        monthlyEquivalent: Math.round(runPerYear / 12),
        taxSavingAnnual: 0,
        netPosition: Math.round(av - cumCost),
        residualBuyout: 0 });
    }
  }

  const exitValue = vehVal(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), yrs, d);
  const postLeaseRunning = runPerYear * Math.max(0, yrs - leaseTerm);
  const totalCost = trueNet * leaseMos + res + postLeaseRunning - exitValue;

  return { method: "novated", yearlyData: yd,
    totalCost: Math.round(totalCost),
    upfrontCost: 0,
    monthlyPayment: Math.round(trueNet),
    grossMonthly: Math.round(preTax), taxSavingMonthly: Math.round(saving),
    fbtMonthly: Math.round(fbtMo), fbtExempt: evExempt,
    gstCreditMonthly: Math.round(gstCreditMo),
    purchaseGstCredit: Math.round(purchaseGstCredit),
    financedPrincipal: Math.round(principal),
    exitValue: Math.round(exitValue),
    residualBuyout: Math.round(res),
    totalInterest: 0,
    effectiveMonthly: Math.round(totalCost / (yrs * 12)),
    taxRate: Math.round((saving / preTax) * 100), onCosts: 0, lct: 0, stampDuty: 0 };
}

/* ─── Formatters ────────────────────────────────────────────────────────── */
const NUM = tokens.font.body; /* unified with tokens.font.body — numeric text uses same font stack */
const fmt = (n) => n == null ? "–" : (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString("en-AU");
const fmtP = (n) => `${(n * 100).toFixed(1)}%`;
const fmtK = (v) => `$${(v / 1000).toFixed(0)}k`;


/* ─── Input Validation ───────────────────────────────────────────────────────
   Guards against nonsensical inputs that would produce NaN/Infinity outputs.
   Called in upd() to sanitise values before they enter state.
─────────────────────────────────────────────────────────────────────────── */
export const BOUNDS = {
  vehiclePrice:       { min: 1000,   max: 500000 },
  holdYears:          { min: 1,      max: 15 },
  annualKm:           { min: 1000,   max: 100000 },
  opportunityCostPct: { min: 0,      max: 0.25 },
  loanRate:           { min: 0,      max: 0.50 },
  loanTermYears:      { min: 1,      max: 10 },
  loanDeposit:        { min: 0,      max: 200000 },
  dealerRate:         { min: 0,      max: 0.50 },
  dealerTermYears:    { min: 1,      max: 10 },
  dealerDeposit:      { min: 0,      max: 200000 },
  balloonPct:         { min: 0,      max: 0.60 },
  leaseRate:          { min: 0,      max: 0.30 },
  leaseResidualPct:   { min: 0.10,   max: 0.80 },
  leaseTermYears:     { min: 1,      max: 7 },
  grossSalary:        { min: 0,      max: 1000000 },
  novatedRate:        { min: 0,      max: 0.30 },
  novatedTermYears:   { min: 1,      max: 7 },
  novatedResidualPct: { min: 0.10,   max: 0.80 },
  novatedProviderFeeAnnual: { min: 0, max: 5000 },
  fuelPerL:           { min: 0.50,   max: 5.00 },
  fuelL100km:         { min: 1,      max: 30 },
  tyresPerYear:       { min: 0,      max: 10000 },
  servicesPerYear:    { min: 0,      max: 20000 },
  insurancePerYear:   { min: 0,      max: 20000 },
  regPerYear:         { min: 0,      max: 5000 },
  depYr1:             { min: 0.01,   max: 0.60 },
  depYr2:             { min: 0.01,   max: 0.50 },
  depYrN:             { min: 0.01,   max: 0.40 },
  evHomeChargeKwh:    { min: 0.05,   max: 1.00 },
  evEfficiencyKwh:    { min: 5,      max: 50 },
  evGovtRebate:       { min: 0,      max: 50000 },
  evPctHomeCharge:    { min: 0,      max: 1.00 },
  evPublicChargeKwh:  { min: 0.20,   max: 1.50 },
  refinanceRate:      { min: 0,      max: 0.50 },
  refinanceYear:      { min: 0,      max: 10 },
};

export function sanitise(key, val) {
  /* Pass strings through directly — isNaN("NSW") is true which would break
     state/evType selectors. Only apply numeric bounds to numeric values. */
  if (val === "" || val === null || val === undefined) return DEFAULTS[key] ?? 0;
  if (typeof val === "string") return val;   /* state, evType etc — pass through */
  if (isNaN(val)) return DEFAULTS[key] ?? 0;
  const b = BOUNDS[key];
  if (!b) return val;
  return Math.min(b.max, Math.max(b.min, val));
}

/* ─── Components ────────────────────────────────────────────────────────── */
const LBL = { fontSize: 10, fontWeight: 600, color: tokens.muted, letterSpacing: "0.01em", fontFamily: tokens.font.body };


/* ─── Tooltip / Explainer Content Library ───────────────────────────────────
   All educational content lives here. Each entry has:
   - title: display heading
   - body: plain-English explanation (can include <strong> tags)
   - suits: who this is good for (optional)
   - notSuited: who should avoid it (optional)
   - tip: a practical tip (optional)
   - learnMore: link slug to full article (optional)
──────────────────────────────────────────────────────────────────────────── */
const EXPLAINERS = {

  cashPurchase: {
    title: "Cash Purchase",
    body: "You pay the full vehicle price upfront from your own savings. You own the car outright from day one, with no ongoing finance obligations and no interest to pay. However, tying up a large lump sum in a depreciating asset has a hidden cost — that money could have been invested elsewhere.",
    suits: "People with savings who want simplicity, no debt, and maximum negotiating power at the dealership. Also suits those who plan to keep the car for many years.",
    notSuited: "People whose savings are earning strong returns elsewhere, or who need to preserve cash for other purposes.",
    tip: "Always get competing finance quotes before deciding to pay cash — sometimes dealer finance offers are genuinely competitive, and keeping your cash invested can come out ahead.",
  },

  personalLoan: {
    title: "Personal Loan",
    body: "You borrow money from a bank or lender at a fixed interest rate, then repay it in equal monthly instalments over an agreed term (typically 3–7 years). You own the car from the start. The interest rate is usually higher than secured finance because the loan is unsecured — but you have full freedom to sell the car any time.",
    suits: "People who want to own the vehicle outright, don't qualify for dealer finance, or want the flexibility to sell without a finance payout complication.",
    notSuited: "People who want the lowest possible monthly payment — personal loan rates are typically higher than dealer finance.",
    tip: "Always compare the comparison rate (not just the advertised rate) across at least 3 lenders. The difference between 8.9% and 12% on a $40k loan over 5 years is around $3,500 in extra interest.",
  },

  dealerFinance: {
    title: "Dealer Finance",
    body: "Finance arranged through the car dealership, typically via a captive finance company (e.g. Toyota Finance, BMW Financial Services) or a third-party lender the dealer has an arrangement with. Often comes with a balloon payment — a lump sum due at the end of the term — which keeps monthly repayments lower but means you don't fully own the car until the balloon is paid.",
    suits: "People who want lower monthly repayments, are comfortable with a balloon at the end, or are purchasing a brand where the manufacturer's finance rate is genuinely competitive.",
    notSuited: "People who don't have a plan for the balloon payment at the end of the term. If you can't pay the balloon, you'll need to refinance — often at a worse rate.",
    tip: "Dealer finance rates can be negotiated, especially at end-of-month or end-of-quarter. The advertised rate is rarely the best available rate.",
  },

  financeLease: {
    title: "Finance Lease",
    body: "A finance lease is an agreement where a financier purchases the vehicle and leases it back to you for a fixed term. You make monthly payments and have a residual value (set by ATO statutory percentages based on km travelled) payable at the end. Unlike a novated lease, there's no salary sacrifice — it's a straightforward finance product typically used by businesses and sole traders.",
    suits: "Business owners, sole traders, and self-employed people who want to claim the vehicle as a business expense. GST on the purchase price may be claimable.",
    notSuited: "PAYG employees — you don't get the tax benefits without an employer arrangement. A novated lease is usually better for employees.",
    tip: "The ATO sets minimum residual values based on annual km travelled. Driving more kilometres means a lower residual and higher monthly payments.",
  },

  novatedLease: {
    title: "Novated Lease",
    body: "A three-way agreement between you, your employer, and a finance company. Your employer deducts lease payments from your pre-tax salary — reducing your taxable income and therefore your income tax. Running costs (fuel, insurance, registration, tyres, servicing) can also be packaged pre-tax. The employer pays FBT (Fringe Benefits Tax) on the benefit, which is typically passed back to you as a post-tax contribution to reduce the FBT liability.",
    suits: "PAYG employees earning $80,000+, especially those in the 30% or higher tax bracket. High-km drivers. People who want to package running costs alongside finance. EV buyers — eligible EVs under $91,661 are exempt from FBT entirely.",
    notSuited: "Self-employed or sole traders (no employer to novate to). Part-time or casual employees — the tax saving is proportional to your income. People who change jobs frequently — the lease stays with you, not the employer, but it can complicate things.",
    tip: "The FBT exemption for eligible electric vehicles under $91,661 (FY2026–27) can save $4,000–$8,000+ over a 3-year lease compared to an equivalent ICE vehicle novated lease. If you're considering an EV, model this scenario carefully.",
  },

  effectiveMonthly: {
    title: "Effective Monthly Cost",
    body: "This is the true average monthly cost of owning the vehicle under this structure, calculated as: (total finance costs + total running costs − exit/sale value) ÷ number of months. It's a more honest number than the monthly repayment because it accounts for what you actually spend and what you recover when you sell.",
    tip: "The monthly repayment figure looks lower because it ignores running costs and depreciation. Always compare effective monthly costs across options — not just repayments.",
  },

  trueTotalCost: {
    title: "True Total Cost",
    body: "The net financial cost of owning the vehicle over your chosen hold period. Calculated as: upfront cost + all finance repayments + all running costs − the vehicle's estimated resale value at exit. This is the most accurate single number for comparing ownership structures — it's what the vehicle actually costs you, not just what you pay each month.",
    tip: "A lower monthly repayment doesn't mean a lower true cost. Dealer finance with a balloon can look cheap monthly but cost more overall if the balloon refinance rate is high.",
  },

  opportunityCost: {
    title: "Opportunity Cost (Cash Purchase)",
    body: "When you pay cash for a car, you give up the ability to invest that money elsewhere. The opportunity cost is what that money could have earned — for example, if you invest $45,000 at 5% per year, you'd earn roughly $2,250 in the first year. Over 5 years the compounding effect is significant. This tool models this as an additional hidden cost of paying cash, which helps make a fairer comparison against financed options.",
    tip: "If your savings are in an offset account reducing mortgage interest at 6%+, the opportunity cost of paying cash for a car is even higher than using a car loan at a similar rate.",
  },

  balloonPayment: {
    title: "Balloon / Residual Payment",
    body: "A lump sum due at the end of a finance or lease term. It keeps your monthly repayments lower during the term, but you must either pay it in full, refinance it (take a new loan to cover it), or sell the vehicle and use the proceeds. The balloon is set as a percentage of the original vehicle price at the start of the loan.",
    suits: "People who plan to sell or trade in the vehicle before or at the end of the term, using the sale proceeds to cover the balloon.",
    notSuited: "People who plan to keep the car beyond the finance term and don't have a plan for the lump sum.",
    tip: "Always check what the car will realistically be worth at the end of the term. If the vehicle value falls below the balloon amount, you're in negative equity — you owe more than the car is worth.",
  },

  fbtSalary: {
    title: "FBT & Salary Sacrifice",
    body: "Salary sacrifice means agreeing with your employer to take a portion of your pre-tax salary as a non-cash benefit — in this case, a car. This reduces your taxable income, saving you income tax. However, the government taxes the benefit through Fringe Benefits Tax (FBT), which the employer pays. The standard way to manage this is the Employee Contribution Method (ECM): you make a post-tax contribution equal to the FBT liability, which reduces it to zero. The net result is a meaningful tax saving — typically $3,000–$8,000+ per year depending on salary and vehicle cost.",
    tip: "The FBT rate (47%) sounds alarming but it's applied to the grossed-up value of the benefit, not the car price. The ECM post-tax contribution you make is usually much smaller than the pre-tax saving you generate.",
  },

  residualATO: {
    title: "ATO Statutory Residual",
    body: "For novated and finance leases, the ATO sets minimum residual values — the amount you must pay at the end of the lease — based on how many kilometres you drive per year. Higher annual km = lower residual = higher monthly payments (but less owing at the end). These percentages are set by the ATO and cannot be lower than the statutory minimums.",
    tip: "If you underestimate your annual km at the start of the lease, the residual will be set too high. You'll owe more at the end than the car is worth — a potentially costly gap.",
  },

  stampDuty: {
    title: "Stamp Duty on Vehicles",
    body: "A state government tax applied to the purchase of a motor vehicle, calculated as a percentage of the vehicle's market value. Each state and territory sets its own rates and brackets, which is why buying the same car in Victoria costs a different amount in stamp duty than buying it in Queensland. For new cars, stamp duty is typically paid at registration. It's a mandatory cost that adds directly to your upfront purchase price.",
    tip: "Some states offer stamp duty concessions for EVs and low-emission vehicles. Check your state's revenue office website for current concessions — the savings can be $1,000–$5,000+.",
  },

  lct: {
    title: "Luxury Car Tax (LCT)",
    body: "A federal government tax applied to vehicles above a set price threshold. For FY2026–27, the threshold is $80,809 for standard vehicles and $91,661 for fuel-efficient vehicles (EVs, PHEVs and other vehicles using 3.5L/100km or less). LCT is charged at 33% on the portion of the price above the threshold, divided by 1.1 to account for GST already paid. It applies to the full supply chain — so if a dealer pays LCT, it will be reflected in the purchase price.",
    tip: "The fuel-efficient threshold ($91,661) applies to EVs and low-emission vehicles — this is one of the key financial incentives for EV buyers alongside the FBT exemption.",
  },

  depreciation: {
    title: "Vehicle Depreciation",
    body: "Cars lose value over time — this is depreciation. The rate of loss is fastest in the first year (typically 15–25% for new cars) as the car transitions from 'new' to 'used'. By years 3–5 the rate slows to around 10% per year. The total depreciation over your hold period is one of the largest components of your true ownership cost — often larger than the interest you pay on finance. This tool models depreciation as a declining-balance curve which you can adjust for your specific vehicle.",
    tip: "Popular models from mainstream brands (Toyota, Mazda, Hyundai) depreciate slower than luxury or European brands. If minimising depreciation matters, a 2–3 year old car has already absorbed the steepest drop and can be a significantly better value proposition.",
  },

  /* ── Chart methodologies (O10) ────────────────────────────────────────── */
  chartTotalCost: {
    title: "Total Cost Comparison — methodology",
    body: "Each bar is the True Total Cost over your selected hold period for that finance method. The formula is: upfront cost + all finance repayments + all running costs − estimated exit value + opportunity cost on cash deployed. Methods that look cheaper on paper (e.g. dealer finance with a 25% balloon) can look more expensive here once running costs and the balloon liability are included.",
    tip: "The headline number can swing materially with small changes to the hold period and opportunity cost rate — drag the sliders to test sensitivity.",
  },
  chartCumulativeSpend: {
    title: "Cumulative Spend — methodology",
    body: "Each line plots cumulative cash out by year, including upfront costs (year 1) plus all finance repayments and running costs accrued. For cash purchases, the line also includes compounded opportunity cost (foregone investment return on the capital). Lines that climb steeply early but flatten later (e.g. cash, large-deposit loans) deploy capital up front; lines that climb steadily (lease, novated) spread the spend.",
    tip: "Where two lines cross, the cheaper method changes. The Y-axis intercept of each line at year N is what you'd report as 'spent so far' at that point.",
  },
  chartAnnualOutflow: {
    title: "Annual Cash Outflow — methodology",
    body: "Each bar is the actual cash spent in that year (not cumulative). Includes finance repayments for the year + annual running costs. Cash purchase shows the full vehicle price in year 1 and only running costs in later years — that's the asymmetric cash-flow profile of a cash purchase. Novated lease shows the post-tax sacrifice equivalent (after-tax monthly × 12) so it's comparable to the after-tax cost of the other structures.",
    tip: "If your year-1 cash flow is constrained, the bar heights for year 1 tell you which structures fit your budget without dipping into savings.",
  },
  chartEffectiveMonthly: {
    title: "Effective Monthly Cost — methodology",
    body: "Cumulative cash spent ÷ months elapsed. For cash purchases this declines over time as the year-1 lump sum gets averaged across more months. For financed structures it converges toward (monthly repayment + monthly running cost). At your full hold-period end-point all lines are within a few dollars of each method's True Total Cost ÷ months — making this the chart that most cleanly shows the long-run 'real' cost difference.",
    tip: "If a line jumps mid-period (e.g. dealer finance at the balloon end), that's the residual buyout or balloon payment landing in cumulative spend.",
  },
  chartAssetVsSpend: {
    title: "Asset Value vs Spend — methodology",
    body: "Two lines per method: the solid line is the depreciated market value of the vehicle at each year, the dashed line is cumulative spend. The gap between them is your 'net position' — money in minus what the car is worth. For Finance Lease and Novated Lease (during the lease) the asset isn't yours yet, so the solid line is hidden / starts at zero.",
    tip: "Methods where the dashed line crosses above the solid line are 'underwater' from that point — net cost has exceeded the car's worth.",
  },
  chartNetWealth: {
    title: "Net Wealth Position — methodology",
    body: "Asset value minus cumulative spend. Above zero means the car is still worth more than you've paid out; below zero means cumulative spend has overtaken the car's value. For financed structures this typically crosses zero around year 2–3 as depreciation outpaces equity built. The reference line at zero is the 'break-even point' — sell before you cross it and you're net positive on the vehicle.",
    tip: "A 5-year cash purchase usually crosses zero earlier than a 5-year personal loan — the loan's deposit + interest are smaller absolute outflows compared to dropping the full price up front.",
  },
  chartSensitivity: {
    title: "Sensitivity Analysis — methodology",
    body: "For each variable, we recompute the winning method's True Total Cost after pushing that input down (left) and up (right) by a small but realistic amount, holding everything else constant. The longer the bar, the more that variable matters. If the bar is short, this input barely moves your answer — don't sweat getting it exactly right. If it's long, this is the input to nail down before you sign anything.",
    tip: "Loan rate and hold period are usually the biggest drivers for financed structures; opportunity cost rate is usually the biggest driver for cash. If a variable's bar is tiny, you can stop second-guessing it.",
  },
};

/* ─── Small helpers ─────────────────────────────────────────────────────── */
const cx = (...names) => names.filter(Boolean).join(" ");

/* ─── InfoModal Component ────────────────────────────────────────────────────
   Full-screen lightbox that displays an explainer entry.
   Triggered by InfoTip buttons throughout the UI. v2: .vc-modal.
─────────────────────────────────────────────────────────────────────────── */
function InfoModal({ id, onClose }) {
  const info = EXPLAINERS[id];
  /* A11y: trap Tab/Shift-Tab inside the modal so keyboard users can't
     accidentally focus the page behind it; Escape closes; focus is restored
     to whatever was focused before the modal opened (handled below). */
  const dialogRef = React.useRef(null);
  const previouslyFocused = React.useRef(null);
  React.useEffect(() => {
    if (!info) return undefined;
    previouslyFocused.current = document.activeElement;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      try { previouslyFocused.current && previouslyFocused.current.focus(); } catch {}
    };
  }, [info, onClose]);
  if (!info) return null;
  return (
    <div className="vc-modal-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className="vc-modal"
        onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby="modal-title"
      >
        <div className="vc-modal__head">
          <h2 id="modal-title" className="vc-modal__title">{info.title}</h2>
          <button type="button" className="vc-close" onClick={onClose} aria-label="Close" autoFocus>✕</button>
        </div>
        <div className="vc-modal__body">
          <p>{info.body}</p>
          {info.suits && (
            <div className="vc-aside">
              <p className="vc-aside__title">Suits</p>
              <p className="vc-aside__text">{info.suits}</p>
            </div>
          )}
          {info.notSuited && (
            <div className="vc-aside">
              <p className="vc-aside__title">May not suit</p>
              <p className="vc-aside__text">{info.notSuited}</p>
            </div>
          )}
          {info.tip && (
            <div className="vc-aside">
              <p className="vc-aside__title">Practical tip</p>
              <p className="vc-aside__text">{info.tip}</p>
            </div>
          )}
          <p className="vc-fine vc-fine--rule">
            General information only — not financial advice. Consult a licensed financial adviser before making financial decisions.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── InfoTip Component ──────────────────────────────────────────────────────
   Small "?" button that opens the InfoModal for a given explainer id.
   Usage: <InfoTip id="novatedLease" openModal={openModal} />
─────────────────────────────────────────────────────────────────────────── */
function InfoTip({ id, openModal, title = "Learn more" }) {
  return (
    <button type="button" className="vc-info" onClick={() => openModal(id)} title={title}>?</button>
  );
}

/* ─── Chart download (O10) ──────────────────────────────────────────────────
   Grabs the SVG rendered by recharts within the wrapping ResponsiveContainer
   and triggers a browser download. Used by ChartHeader below. */
function downloadChartSvg(chartRef, filename) {
  try {
    const node = chartRef && chartRef.current;
    const svg = node && node.querySelector('svg');
    if (!svg) return;
    /* Clone so we can inline computed styles without mutating the live chart. */
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    /* Add a white background — recharts renders transparent which prints odd. */
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', tokensV2.surface);
    clone.insertBefore(bg, clone.firstChild);
    const xml = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename + '.svg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try { window.gtag('event', 'chart_download', { chart: filename }); } catch {}
    }
  } catch {}
}

/* ChartHeader — title + InfoTip + SVG download button. Replaces the bare
   <div>{title}</div> we had above each ResponsiveContainer. The download button
   only shows for a chart that draws an SVG (chartRef given). */
function ChartHeader({ title, sub, infoId, chartRef, downloadName, openModal }) {
  return (
    <>
      <div className="vc-chart__head">
        <h2 className="vc-chart__title">{title}</h2>
        {infoId && <InfoTip id={infoId} openModal={openModal} />}
        {chartRef && (
          <button
            type="button"
            className="vc-btn vc-chart__download"
            onClick={() => downloadChartSvg(chartRef, downloadName)}
            title="Download as SVG"
            aria-label="Download chart as SVG"
          >
            Download
          </button>
        )}
      </div>
      {sub && <p className="vc-chart__sub">{sub}</p>}
    </>
  );
}

/* ─── Charts (DESIGN.md §5) ─────────────────────────────────────────────────
   The option being discussed (the cheapest, the hero) is amber; every other
   option is ink, told apart by dash pattern and named by a label at the end of
   its line. No colour legend. The two charts where that can't work (grouped
   bars, and two lines per option) tell the ink options apart by pattern and
   name them in a key drawn in those patterns (Pat, 22 Oct 2026). */
const DASHES = ["6 4", "2 4", "10 4 2 4", "1 3"];
const BAR_LOOKS = [
  { fill: tokensV2.ink },
  { fill: tokensV2.surface, stroke: tokensV2.ink, strokeWidth: 1.5 },
  { fill: "url(#vc-hatch)", stroke: tokensV2.ink, strokeWidth: 1 },
  { fill: tokensV2.surface, stroke: tokensV2.ink, strokeWidth: 1.5, strokeDasharray: "2 2" },
];
const MARKERS = ["circle", "square", "triangle", "diamond"];

/* Each option's look in a chart: the hero amber, the others ink with the next
   dash, bar fill and marker in turn. */
function chartLooks(results, heroMethod) {
  const looks = {};
  let k = 0;
  results.forEach((r) => {
    if (r.method === heroMethod) {
      looks[r.method] = { hero: true, stroke: tokensV2.amber, width: 3, bar: { fill: tokensV2.amber } };
    } else {
      looks[r.method] = { hero: false, stroke: tokensV2.ink, width: 2, dash: DASHES[k % DASHES.length],
        bar: BAR_LOOKS[k % BAR_LOOKS.length], marker: MARKERS[k % MARKERS.length] };
      k += 1;
    }
  });
  return looks;
}

/* A marker shape at a point (the ink options on Asset Value vs Total Spent). */
function MarkerShape({ shape, cx: x, cy: y, stroke }) {
  if (x == null || y == null) return null;
  const common = { fill: tokensV2.surface, stroke, strokeWidth: 1.5 };
  if (shape === "square") return <rect x={x - 3.5} y={y - 3.5} width={7} height={7} {...common} />;
  if (shape === "triangle") return <polygon points={`${x},${y - 4.5} ${x + 4.5},${y + 3.5} ${x - 4.5},${y + 3.5}`} {...common} />;
  if (shape === "diamond") return <polygon points={`${x},${y - 5} ${x + 5},${y} ${x},${y + 5} ${x - 5},${y}`} {...common} />;
  return <circle cx={x} cy={y} r={3.5} {...common} />;
}
const markerDot = (shape) => (props) => (
  <g key={props.key}><MarkerShape shape={shape} cx={props.cx} cy={props.cy} stroke={props.stroke} /></g>
);

/* The hatch the third ink bar uses (and its key swatch). */
function HatchDef({ id = "vc-hatch" }) {
  return (
    <pattern id={id} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width="6" height="6" fill={tokensV2.surface} />
      <line x1="0" y1="0" x2="0" y2="6" stroke={tokensV2.ink} strokeWidth="2" />
    </pattern>
  );
}

/* Names at the ends of the lines, nudged apart so they never overlap. Drawn
   inside the chart's SVG (Recharts <Customized>), so a downloaded chart keeps them. */
function EndLabels({ formattedGraphicalItems = [], offset, names }) {
  const GAP = 16;
  const labels = formattedGraphicalItems.map((g) => {
    const pts = (g.props && g.props.points) || [];
    let last = null;
    for (let i = pts.length - 1; i >= 0; i -= 1) {
      if (pts[i] && pts[i].y != null && !isNaN(pts[i].y)) { last = pts[i]; break; }
    }
    const key = g.item && g.item.props && g.item.props.dataKey;
    return last && names[key] ? { key, text: names[key].text, hero: names[key].hero, x: last.x, y: last.y } : null;
  }).filter(Boolean).sort((a, b) => a.y - b.y);
  /* Labels that would overlap form a cluster, spaced GAP apart and centred on
     the mean of their lines' ends; clusters that then touch merge. */
  const clusters = [];
  labels.forEach((l) => {
    clusters.push({ items: [l], top: l.y });
    for (;;) {
      const n = clusters.length;
      if (n < 2) break;
      const a = clusters[n - 2], b = clusters[n - 1];
      if (a.top + a.items.length * GAP <= b.top) break;
      const items = a.items.concat(b.items);
      const mean = items.reduce((t, x) => t + x.y, 0) / items.length;
      clusters.splice(n - 2, 2, { items, top: mean - ((items.length - 1) * GAP) / 2 });
    }
  });
  const top = offset ? offset.top : -Infinity;
  const bottom = offset ? offset.top + offset.height : Infinity;
  clusters.forEach((c) => {
    const first = Math.min(Math.max(c.top, top), bottom - (c.items.length - 1) * GAP);
    c.items.forEach((l, i) => { l.y = first + i * GAP; });
  });
  return (
    <g>
      {labels.map((l) => (
        <text key={l.key} x={l.x + 8} y={l.y} dy="0.35em" fontSize={13}
          fontWeight={l.hero ? tokensV2.fw.head : tokensV2.fw.label} fill={tokensV2.ink} fontFamily={NUM}>
          {l.text}
        </text>
      ))}
    </g>
  );
}

/* The key under a patterned chart: each option's swatch (amber for the hero, an
   ink pattern for the rest) and its name. kind: "bar" or "line". */
function ChartKey({ results, looks, kind }) {
  return (
    <ul className="vc-chart-key">
      {results.map((r) => {
        const look = looks[r.method];
        return (
          <li key={r.method}>
            {kind === "bar" ? (
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                {look.bar.fill === "url(#vc-hatch)" && <defs><HatchDef id={`vc-hatch-key-${r.method}`} /></defs>}
                <rect x="1" y="1" width="14" height="14" rx="2"
                  fill={look.bar.fill === "url(#vc-hatch)" ? `url(#vc-hatch-key-${r.method})` : look.bar.fill}
                  stroke={look.bar.stroke || "none"} strokeWidth={look.bar.strokeWidth || 0} strokeDasharray={look.bar.strokeDasharray} />
              </svg>
            ) : (
              <svg width="28" height="14" viewBox="0 0 28 14" aria-hidden="true">
                <line x1="1" y1="7" x2="27" y2="7" stroke={look.stroke} strokeWidth={look.width} />
                {!look.hero && <MarkerShape shape={look.marker} cx={14} cy={7} stroke={look.stroke} />}
              </svg>
            )}
            {METHOD_META[r.method].label}
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Fields ────────────────────────────────────────────────────────────────
   v2 fields (.vc-field, .vc-input, .vc-range, .vc-check, .vc-seg). Results
   recompute as people type (typed figures commit ~150 ms after the last key). */
function FieldLabel({ htmlFor, id, label, info, openModal }) {
  return (
    <span className="vc-field__labelline">
      <label htmlFor={htmlFor} id={id}>{label}</label>
      {info && openModal && <InfoTip id={info} openModal={openModal} />}
    </span>
  );
}

/* A typed figure, live: it commits as people type, keeps their text while the
   field has focus, and tidies it on blur or Enter. */
function useLiveText(value, onChange, parse) {
  const [local, setLocal] = React.useState(null);
  const timer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(timer.current), []);
  const change = (e) => {
    const s = e.target.value;
    setLocal(s);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { const p = parse(s); if (p != null) onChange(p); }, 150);
  };
  const commit = () => {
    clearTimeout(timer.current);
    if (local != null) { const p = parse(local); if (p != null) onChange(p); }
    setLocal(null);
  };
  return { local, change, commit };
}

function Slider({ label, value, onChange, min, max, step = 1, fmt: f = (v) => v, sub, info, openModal, isRate = false }) {
  /* isRate=true adds a text box for exact rate entry to 2 decimal places */
  const id = React.useId();
  const parseRate = (s) => {
    const parsed = parseFloat(s);
    return !isNaN(parsed) && parsed >= 0 && parsed <= 50 ? Math.round(parsed * 100) / 10000 : null;  /* convert %% to decimal: 8.99 → 0.0899 */
  };
  const rate = useLiveText(value, onChange, parseRate);
  return (
    <div className="vc-field">
      <div className="vc-field__row">
        <FieldLabel htmlFor={`${id}-range`} id={`${id}-label`} label={label} info={info} openModal={openModal} />
        <span className="vc-figure">{f(value)}</span>
      </div>
      <input id={`${id}-range`} type="range" className="vc-range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={String(f(value))} aria-describedby={sub ? `${id}-hint` : undefined} />
      {isRate && (
        <div className="vc-input vc-input--short">
          <input type="text" inputMode="decimal" aria-labelledby={`${id}-label`}
            value={rate.local != null ? rate.local : (value * 100).toFixed(2)}
            onChange={rate.change} onBlur={rate.commit} onKeyDown={(e) => e.key === "Enter" && rate.commit()} />
          <span className="vc-input__affix">%</span>
        </div>
      )}
      {sub && <span className="vc-field__hint" id={`${id}-hint`}>{sub}</span>}
    </div>
  );
}

/* Free-typing number input — avoids browser type=number stepping issues */
function NIInput({ id, value, onChange, describedBy }) {
  const parse = (s) => { const p = parseFloat(String(s).replace(/[^0-9.]/g, "")); return isNaN(p) ? null : p; };
  const t = useLiveText(value, onChange, parse);
  return (
    <input id={id} type="text" inputMode="numeric" value={t.local != null ? t.local : String(value)}
      onChange={t.change}
      onBlur={t.commit}
      onKeyDown={(e) => e.key === "Enter" && t.commit()}
      aria-describedby={describedBy} />
  );
}

function NI({ label, value, onChange, pre = "$", suf, sub, info, openModal }) {
  const id = React.useId();
  return (
    <div className="vc-field">
      <FieldLabel htmlFor={id} label={label} info={info} openModal={openModal} />
      <div className="vc-input">
        {pre && <span className="vc-input__affix">{pre}</span>}
        <NIInput id={id} value={value} onChange={onChange} describedBy={sub ? `${id}-hint` : undefined} />
        {suf && <span className="vc-input__affix">{suf}</span>}
      </div>
      {sub && <span className="vc-field__hint" id={`${id}-hint`}>{sub}</span>}
    </div>
  );
}

function Toggle({ label, value, onChange, sub }) {
  const id = React.useId();
  return (
    <div className="vc-check">
      <input type="checkbox" id={id} checked={!!value} onChange={(e) => onChange(e.target.checked)}
        aria-describedby={sub ? `${id}-hint` : undefined} />
      <div className="vc-check__body">
        <label htmlFor={id}>{label}</label>
        {sub && <span className="vc-field__hint" id={`${id}-hint`}>{sub}</span>}
      </div>
    </div>
  );
}

function Sel({ label, value, onChange, options }) {
  const id = React.useId();
  return (
    <div className="vc-field">
      <label htmlFor={id}>{label}</label>
      <div className="vc-input">
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
        </select>
      </div>
    </div>
  );
}

function SHead({ children, info, openModal }) {
  return (
    <h2 className="vc-inputs__head">
      {children}
      {info && openModal && <InfoTip id={info} openModal={openModal} />}
    </h2>
  );
}

/* A card in the results column (charts and depth-3 panels). */
function Panel({ children }) {
  return <section className="vc-card vc-chart">{children}</section>;
}

/* Label and value rows, as a compact v2 table. */
function Rows({ children }) {
  return (
    <table className="vc-table vc-table--compact">
      <tbody>{children}</tbody>
    </table>
  );
}

function DRow({ label, value, bold, info, openModal }) {
  return (
    <tr className={bold ? "vc-table__total" : undefined}>
      <th scope="row">
        {label}
        {info && openModal && <> <InfoTip id={info} openModal={openModal} /></>}
      </th>
      <td>{value}</td>
    </tr>
  );
}

/* A figure with its label: a reading with no gauge. */
function KPI({ label, value, sub }) {
  return (
    <div className="vc-reading">
      <div className="vc-reading__body">
        <div className="vc-reading__label">{label}</div>
        <div className="vc-reading__value">{value}</div>
        {sub && <div className="vc-reading__note">{sub}</div>}
      </div>
    </div>
  );
}

const METHOD_EXPLAINER = {
  cash: "cashPurchase", loan: "personalLoan", dealer: "dealerFinance",
  lease: "financeLease", novated: "novatedLease",
};

/* A method to compare: a checkbox with its "?". */
function MBadge({ method, active, onClick, openModal }) {
  const m = METHOD_META[method];
  const id = `method-${method}`;
  return (
    <li>
      <div className="vc-check">
        <input type="checkbox" id={id} checked={active} onChange={onClick} />
        <div className="vc-check__body"><label htmlFor={id}>{m.label}</label></div>
      </div>
      <InfoTip id={METHOD_EXPLAINER[method]} openModal={openModal} title="What is this?" />
    </li>
  );
}

/* Results / Exit Sim. Arrow keys move between tabs. */
function Tabs({ tabs, active, onChange }) {
  const refs = React.useRef([]);
  const onKeyDown = (e, i) => {
    const next = e.key === "ArrowRight" ? (i + 1) % tabs.length
      : e.key === "ArrowLeft" ? (i - 1 + tabs.length) % tabs.length : null;
    if (next == null) return;
    e.preventDefault();
    onChange(tabs[next].id);
    if (refs.current[next]) refs.current[next].focus();
  };
  return (
    <div className="vc-tabs" role="tablist">
      {tabs.map((t, i) => {
        const isActive = active === t.id;
        return (
          <button key={t.id} ref={(n) => { refs.current[i] = n; }} type="button" role="tab"
            id={`tab-${t.id}`} aria-selected={isActive} aria-controls="tab-panel" tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(t.id)} onKeyDown={(e) => onKeyDown(e, i)}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="vc-chart-tip">
      <div className="vc-chart-tip__title">Year {label}</div>
      {payload.map((p) => <div key={p.dataKey}>{p.name}: {fmt(p.value)}</div>)}
    </div>
  );
};
const TIP_STYLE = {
  contentStyle: { background: tokensV2.surface, border: `1px solid ${tokensV2.lineStrong}`, borderRadius: 10, fontFamily: NUM },
  itemStyle: { color: tokensV2.ink },
  labelStyle: { color: tokensV2.ink, fontWeight: tokensV2.fw.head },
  cursor: { fill: tokensV2.tint },
};

/* True total cost of each option as chart tubes: every tube the same height (the
   largest total), the hero amber, the rest ink (DESIGN.md §5). */
function CostTubes({ sorted, label }) {
  const max = Math.max(0, ...sorted.map((r) => r.totalCost));
  const hero = sorted[0] && sorted[0].method;
  const width = sorted.length > 4 ? 44 : 56;
  return (
    <div className="vc-levels" role="img"
      aria-label={`${label}: ${sorted.map((r) => `${METHOD_META[r.method].label} ${fmt(r.totalCost)}`).join(", ")}`}>
      {sorted.map((r) => (
        <div key={r.method} className="vc-levels__col">
          <div className="vc-levels__value">{fmt(r.totalCost)}</div>
          <Level value={max > 0 ? Math.max(0, r.totalCost) / max : 0} width={width} height={200} gap={4}
            top={tokensV2.tint} fill={r.method === hero ? tokensV2.amber : tokensV2.ink} />
          <div className="vc-levels__name">{METHOD_META[r.method].label}</div>
        </div>
      ))}
    </div>
  );
}

function SCard({ result, rank, openModal }) {
  const m = METHOD_META[result.method];
  const medals = ["1st", "2nd", "3rd"];
  return (
    <article className={cx("vc-card vc-option", rank === 0 && "vc-card--best")}>
      <div className="vc-option__head">
        <h3 className="vc-option__title">{m.label}</h3>
        {medals[rank] && <span className="vc-small">{medals[rank]}</span>}
      </div>
      <p className="vc-figure" style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>{fmt(result.totalCost)}</p>
      <Rows>
        <DRow label="Eff. monthly" value={fmt(result.effectiveMonthly)} info="effectiveMonthly" openModal={openModal} />
        <DRow label="Upfront" value={fmt(result.upfrontCost)} />
        {result.monthlyPayment > 0 && <DRow label={result.monthlyPaymentRefi != null ? `Payment/mo (Y1–${result.refinanceYear})` : "Payment/mo"} value={fmt(result.monthlyPayment)} />}
        {result.monthlyPaymentRefi != null && <DRow label="Payment/mo (post-refi)" value={fmt(result.monthlyPaymentRefi)} />}
        {result.totalInterest > 0 && <DRow label="Total interest" value={fmt(result.totalInterest)} />}
        {result.exitValue > 0 && <DRow label="Exit value" value={fmt(result.exitValue)} />}
        {result.residualBuyout > 0 && <DRow label="Residual" value={fmt(result.residualBuyout)} />}
        {result.taxSavingMonthly > 0 && <DRow label="Tax saving/mo" value={fmt(result.taxSavingMonthly)} />}
        {result.onCosts > 0 && <DRow label="Stamp+LCT" value={fmt(result.onCosts)} />}
      </Rows>
      {result.method === "lease" && result.leaseTermYears > 0 && (
        <div className="vc-aside">
          <p className="vc-aside__text">
            Lease ends at year {result.leaseTermYears}. Costs shown stop at handback — use <a href="/end-of-term">/end-of-term</a> to model what happens next.
          </p>
        </div>
      )}
    </article>
  );
}

/* A meter reading (approved meters only, src/design/v2/meterRules.js): the
   gauge is a button that shows the rule underneath. */
function MeterReading({ id, title, r }) {
  const [open, setOpen] = useState(false);
  const ruleId = `${id}-rule`;
  return (
    <div className="vc-reading">
      <button type="button" className="vc-gauge" aria-expanded={open} aria-controls={ruleId}
        aria-label={`${r.aria}. Show the rule`} onClick={() => setOpen((o) => !o)}>
        <Meter value={r.lit} label={r.aria} />
      </button>
      <div className="vc-reading__body">
        <div className="vc-reading__title">{title} · {r.label}</div>
        <div className="vc-reading__note">{r.sentence}</div>
        <p className="vc-reading__rule" id={ruleId} hidden={!open}>{r.tap}</p>
      </div>
    </div>
  );
}

/* Things to check: equity at exit and the rate check, for Personal Loan and
   Dealer Finance only (PHASE-4.md). Cash owes nothing; Finance Lease is a
   business product and the benchmark is consumer loans; novated has no public
   rate benchmark (its equity meter is on /novated). Both equity figures are the
   engine's, at the end of the hold period. */
function checkRows(allR, active, d) {
  const groups = [];
  [["loan", d.loanRate], ["dealer", d.dealerRate]].forEach(([m, rate]) => {
    if (!active.includes(m)) return;
    const r = allR[m];
    const items = [];
    const equity = equityAtExit({ owing: r.loanBalance, value: r.exitValue, when: `at year ${d.holdYears}`,
      source: 'Veercal\'s estimate from your depreciation settings' });
    if (equity) items.push({ id: `check-${m}-equity`, title: `${fmt(r.loanBalance)} owing at year ${d.holdYears}`, r: equity });
    const ratePct = rate * 100;
    const rc = rateCheck({ rate: ratePct, benchmark: RATE_BENCHMARK.pct, period: RATE_BENCHMARK.period });
    if (rc) items.push({ id: `check-${m}-rate`, title: `Interest rate ${Number(ratePct.toFixed(2))}%`, r: rc });
    if (items.length) groups.push({ method: m, items });
  });
  return groups;
}

function ChecksBand({ groups }) {
  return (
    <section className="vc-calc__block" aria-labelledby="checks-title">
      <h2 className="vc-h3" id="checks-title">Things to check</h2>
      <div className="vc-checks">
        {groups.map((g) => (
          <div key={g.method}>
            <h3 className="vc-checks__option">{METHOD_META[g.method].label}</h3>
            {g.items.map((it) => <MeterReading key={it.id} id={it.id} title={it.title} r={it.r} />)}
          </div>
        ))}
      </div>
    </section>
  );
}

/* SensitivityPanel — recomputes the winner's total cost with each input
   pushed down/up by a small but realistic amount, holding everything else
   constant. A long bar = this variable matters; a short one = it barely
   moves the answer. The full set of base→perturbed runs is wrapped in
   useMemo so it only refires when inputs or the winner change. */
function SensitivityPanel({ d, winner, openModal }) {
  const VARS = useMemo(() => [
    { key: 'annualKm',           label: 'Annual km',           delta: 5000,   fmt: v => `${v.toLocaleString()} km` },
    { key: 'holdYears',          label: 'Hold period',         delta: 1,      fmt: v => `${v} yr`,                  min: 1, max: 15 },
    { key: 'opportunityCostPct', label: 'Opportunity cost',    delta: 0.01,   fmt: v => `${(v * 100).toFixed(1)}%` },
    { key: 'loanRate',           label: 'Loan rate',           delta: 0.01,   fmt: v => `${(v * 100).toFixed(2)}%` },
    { key: 'novatedRate',        label: 'Novated rate',        delta: 0.01,   fmt: v => `${(v * 100).toFixed(2)}%` },
  ], []);

  const calcWinner = useCallback((dd) => {
    switch (winner.method) {
      case 'cash':    return calcCash(dd).totalCost;
      case 'loan':    return calcLoan(dd, 'loan').totalCost;
      case 'dealer':  return calcLoan(dd, 'dealer').totalCost;
      case 'lease':   return calcLease(dd).totalCost;
      case 'novated': return calcNovated(dd).totalCost;
      default:        return 0;
    }
  }, [winner.method]);

  const rows = useMemo(() => VARS.map(v => {
    const cur = d[v.key];
    if (cur == null) return null;
    const minus = Math.max(v.min ?? -Infinity, cur - v.delta);
    const plus  = Math.min(v.max ??  Infinity, cur + v.delta);
    return {
      ...v,
      current: cur,
      lo: minus,
      hi: plus,
      base: winner.totalCost,
      tcLo: calcWinner({ ...d, [v.key]: minus }),
      tcHi: calcWinner({ ...d, [v.key]: plus }),
    };
  }).filter(Boolean), [VARS, d, winner.totalCost, calcWinner]);

  /* Normalise bar widths against the biggest single-direction swing across all rows. */
  const maxSwing = Math.max(1, ...rows.map(r => Math.max(Math.abs(r.tcLo - r.base), Math.abs(r.tcHi - r.base))));

  return (
    <Panel>
      {/* No SVG to download here, so no download button (it never had a chart to save). */}
      <ChartHeader title="Sensitivity Analysis" sub={`How each input moves ${METHOD_META[winner.method].label}'s total cost`} infoId="chartSensitivity" openModal={openModal} />
      {/* Bars in ink; the signed figures say which way each input moves the total
          (Pat, 22 Oct 2026: no green and red). */}
      <div className="vc-tornado">
        <div className="vc-tornado__head">Variable</div>
        <div className="vc-tornado__head" style={{ textAlign: "right" }}>Lower → current</div>
        <div className="vc-tornado__head">Current → higher</div>
        {rows.map(r => {
          const dLo = r.tcLo - r.base;
          const dHi = r.tcHi - r.base;
          const wLo = Math.abs(dLo) / maxSwing;
          const wHi = Math.abs(dHi) / maxSwing;
          return (
            <React.Fragment key={r.key}>
              <div>
                <span className="vc-tornado__name">{r.label}</span>
                <span className="vc-tornado__range">{r.fmt(r.lo)} ↔ {r.fmt(r.hi)}</span>
              </div>
              {/* Left bar — extends right-to-left from the centre */}
              <div className="vc-tornado__side vc-tornado__side--lo">
                <span>{dLo > 0 ? "+" : ""}{fmt(dLo)}</span>
                <div className="vc-tornado__bar" style={{ width: `${wLo * 100}%` }} />
              </div>
              {/* Right bar — extends left-to-right from the centre */}
              <div className="vc-tornado__side">
                <div className="vc-tornado__bar" style={{ width: `${wHi * 100}%` }} />
                <span>{dHi > 0 ? "+" : ""}{fmt(dHi)}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <p className="vc-chart__sub">
        Reading: rows with longer bars are the inputs that most affect <strong>{METHOD_META[winner.method].label}</strong>'s total cost.
      </p>
    </Panel>
  );
}

function ExitSim({ results, d }) {
  const [yr, setYr] = useState(Math.min(3, d.holdYears));
  return (
    <>
      <Slider label="Simulate exit at year" value={yr} onChange={setYr} min={1} max={d.holdYears} fmt={(v) => `Year ${v}`} />
      <div className="vc-options">
        {results.map((r) => {
          const m = METHOD_META[r.method], yd = r.yearlyData[yr - 1];
          if (!yd) return null;
          const netCost = yd.totalSpent - yd.assetValue;
          return (
            <article key={r.method} className="vc-card vc-option">
              <div className="vc-option__head"><h3 className="vc-option__title">{m.label}</h3></div>
              <Rows>
                <DRow label="Market / sale value" value={fmt(yd.assetValue)} />
                {yd.residualBuyout > 0 && <DRow label="Residual buyout required" value={fmt(yd.residualBuyout)} />}
                {yd.residualBuyout > 0 && <DRow label="Market vs residual gap" value={fmt(yd.assetValue - yd.residualBuyout)} />}
                <DRow label="Total spent to date" value={fmt(yd.totalSpent)} />
                <DRow label="Net cost of ownership" value={fmt(netCost)} bold />
                <DRow label="Cost per month" value={fmt(Math.round(yd.totalSpent / (yr * 12)))} />
                <DRow label="Cost per km" value={`$${(netCost / (yr * d.annualKm)).toFixed(2)}`} />
              </Rows>
            </article>
          );
        })}
      </div>
    </>
  );
}

/* ─── Disclaimer Banner ──────────────────────────────────────────────────── */
function DisclaimerBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return (
    <div className="vc-notice no-print">
      <div className="vc-container vc-notice__inner">
        <p style={{ margin: 0 }}>
          <strong>General information only — not financial advice.</strong>{" "}
          <button type="button" className="vc-link" onClick={() => setDismissed(false)}>Read full disclaimer</button>
        </p>
      </div>
    </div>
  );
  return (
    <div className="vc-notice no-print">
      <div className="vc-container vc-notice__inner">
        <div className="vc-notice__body">
          <p className="vc-notice__title">General information only — not financial advice</p>
          <p>
            Veercal is a calculation and comparison tool designed to help you understand and model different car ownership structures.{" "}
            <strong>It does not constitute financial, tax, or legal advice</strong> and must not be relied upon as such.
            All figures are <strong>indicative estimates only</strong>, based on simplified assumptions and publicly available rates.
            Actual costs will vary based on your personal circumstances, specific lender/lessor products, current ATO rates, state government charges, and other factors not modelled here.
          </p>
          <p>
            <strong>Stamp duty</strong> uses simplified brackets and applies NSW/ACT EV exemptions and the VIC green-car flat rate. For refined QLD cylinder and ACT emissions-category selection, use <a href="/stamp-duty">/stamp-duty</a>. Pensioner discounts are not modelled.{" "}
            <strong>Novated lease</strong> uses the ATO statutory FBT method — employer arrangements, fleet discounts, and provider fees vary.{" "}
            <strong>LCT thresholds</strong> reflect FY2026–27 ATO rates, updated annually each July.{" "}
            <strong>Depreciation</strong> is a simplified declining-balance model — actual resale values depend on make, model, condition, and market.
          </p>
          <p>
            <strong>Always consult a licensed financial adviser, accountant, or tax professional before making any financial decision. Veercal accepts no liability for decisions made based on outputs of this tool.</strong>
          </p>
        </div>
        <button type="button" className="vc-btn" onClick={() => setDismissed(true)}>Got it</button>
      </div>
    </div>
  );
}


/* ─── URL Share Encoding ─────────────────────────────────────────────────────
   Encodes all scenario inputs into a compact base64 URL hash so any scenario
   can be shared as a link. Decodes on load if hash present.
─────────────────────────────────────────────────────────────────────────── */
function encodeScenario(d) {
  try {
    /* Strip prior schema markers so re-shares don't accumulate stale data, then
       stamp the current SCENARIO_SCHEMA_VERSION onto the encoded payload. */
    const { _v, _versionMismatch, ...clean } = d;
    void _v; void _versionMismatch;
    const str = JSON.stringify({ ...clean, _v: SCENARIO_SCHEMA_VERSION });
    return btoa(unescape(encodeURIComponent(str)));
  } catch { return null; }
}

function decodeScenario(hash) {
  try {
    const str = decodeURIComponent(escape(atob(hash)));
    const parsed = JSON.parse(str);
    /* Mismatch = missing _v (pre-versioning link) OR newer/older than current.
       The link still works — we just surface a transient banner so the user
       knows the figures might differ from when they shared it. */
    const versionMismatch =
      parsed._v == null || parsed._v !== SCENARIO_SCHEMA_VERSION;
    // Merge with DEFAULTS so any new keys added later still have values
    return { ...DEFAULTS, ...parsed, _versionMismatch: versionMismatch };
  } catch { return null; }
}

function getInitialState() {
  try {
    const hash = window.location.hash?.slice(1);
    if (hash && hash.startsWith("s:")) {
      const decoded = decodeScenario(hash.slice(2));
      if (decoded) { track('load_scenario_url', { surface: 'calculator' }); return decoded; }
    }
  } catch {}
  return DEFAULTS;
}

/* ─── Analytics helper ──────────────────────────────────────────────────────
   Thin wrapper around gtag — silently no-ops if gtag isn't loaded (e.g.
   in tests, ad-blocked environments, or before the script lands). Use sparingly
   for high-signal events; don't track every keystroke. */
function track(eventName, params = {}) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch {}
}


/* ─── Error Boundary ─────────────────────────────────────────────────────────
   Catches any runtime errors in the calculator and shows a clean fallback
   rather than crashing the entire page.
─────────────────────────────────────────────────────────────────────────── */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Veercal error:", error, info); }
  render() {
    if (this.state.hasError) return (
      <div className="vc-container" style={{ padding: "40px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16, textAlign: "center" }}>
        <p className="vc-h3" style={{ margin: 0 }}>Something went wrong</p>
        <p style={{ margin: 0 }}>
          An unexpected error occurred in the calculator. Please refresh the page.
        </p>
        <button type="button" className="vc-btn vc-btn--primary" onClick={() => this.setState({ hasError: false, error: null })}>
          Try again
        </button>
        <p className="vc-fine" style={{ margin: 0 }}>
          {this.state.error?.message || "Unknown error"}
        </p>
      </div>
    );
    return this.props.children;
  }
}

/* ─── Main App ───────────────────────────────────────────────────────────── */
export default function App() {
  const [d, setD] = useState(() => getInitialState());
  const [active, setActive] = useState(["cash", "loan", "novated"]);
  const [tab, setTab] = useState("results");
  const [depth, setDepth] = useState(1);   /* bento progressive-disclosure depth */
  const [st, setSt] = useState("vehicle");
  const [sidebarOpen, setSidebarOpen] = useState(false);        /* mobile sidebar */
  const [activeModal, setActiveModal] = useState(null);         /* info lightbox */
  const [cookieConsent, setCookieConsent] = useState(() => {    /* privacy notice */
    /* Bar is hidden once the user has either Accepted or Declined. We only
       read "accepted"/"declined"; anything else means the user hasn't seen
       it yet on this device. */
    try {
      const v = localStorage.getItem("veercal_cookie_consent");
      return v === "accepted" || v === "declined";
    } catch { return false; }
  });
  const acceptCookies = () => {
    setCookieConsent(true);
    try { localStorage.setItem("veercal_cookie_consent", "accepted"); } catch {}
  };
  const declineCookies = () => {
    setCookieConsent(true);
    try { localStorage.setItem("veercal_cookie_consent", "declined"); } catch {}
  };
  const openModal = (id) => setActiveModal(id);
  const closeModal = useCallback(() => setActiveModal(null), []);
  const [scenarios, setScenarios] = useState(() => {             /* saved scenarios */
    try { return JSON.parse(localStorage.getItem("veercal_scenarios") || "[]"); }
    catch { return []; }
  });
  const [shareToast, setShareToast] = useState(false);           /* share feedback */
  const [showScenarios, setShowScenarios] = useState(false);     /* scenario panel */
  const [saveNameInput, setSaveNameInput] = useState("");         /* inline save name */
  const [showSaveInput, setShowSaveInput] = useState(false);      /* save name visible */
  const [versionNotice, setVersionNotice] = useState(() => !!d._versionMismatch); /* O16 */
  const [showChanges, setShowChanges] = useState(false);                         /* O15 modal */
  /* O15: surface a "what's new" pill if there's a CHANGES entry newer than the
     user's last-seen marker stored in localStorage. */
  const newestChangeDate = CHANGES.length > 0 ? CHANGES[0].date : null;
  const [changeBannerDismissed, setChangeBannerDismissed] = useState(() => {
    try { return localStorage.getItem("veercal_lastseen_change") === newestChangeDate; }
    catch { return false; }
  });
  const dismissChangeBanner = () => {
    setChangeBannerDismissed(true);
    try { localStorage.setItem("veercal_lastseen_change", newestChangeDate); } catch {}
  };

  const upd = useCallback((k, v) => setD((p) => ({ ...p, [k]: sanitise(k, v) })), []);

  /* Refs for SVG download (O10) — one per chart. Attached to the wrapping
     div around each ResponsiveContainer; downloadChartSvg picks up the
     first <svg> recharts renders inside. */
  const chartRefTotalCost      = React.useRef(null);
  const chartRefCumSpend       = React.useRef(null);
  const chartRefAnnualOutflow  = React.useRef(null);
  const chartRefEffMonthly     = React.useRef(null);
  const chartRefAssetSpend     = React.useRef(null);
  const chartRefNetWealth      = React.useRef(null);

  /* Phone inputs drawer: focus its close button when it opens, Escape closes it
     and returns focus to the "Edit inputs" button. */
  const openInputsRef = React.useRef(null);
  const closeInputsRef = React.useRef(null);
  React.useEffect(() => {
    if (!sidebarOpen) return undefined;
    if (closeInputsRef.current) closeInputsRef.current.focus();
    const onKey = (e) => { if (e.key === "Escape") { setSidebarOpen(false); if (openInputsRef.current) openInputsRef.current.focus(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  /* setIsEV — toggles EV mode and, if the user hasn't customised them, bumps
     the EV-sensitive defaults:
       • Year-1 depreciation: 15% → 20% (BEVs ran ~20–25% Y1 in 2023–25 AU data)
       • Insurance: $1,800 → $2,070 (~15% higher than ICE for the same value;
         premiums reflect higher repair/parts costs).
     User can still override after. Reverts on toggle off if still at the
     EV-bumped value. */
  const setIsEV = useCallback((v) => setD((p) => {
    const nextDepYr1 =
      v && p.depYr1 === 0.15 ? 0.20 :
      !v && p.depYr1 === 0.20 ? 0.15 :
      p.depYr1;
    const nextInsurance =
      v && p.insurancePerYear === 1800 ? 2070 :
      !v && p.insurancePerYear === 2070 ? 1800 :
      p.insurancePerYear;
    return { ...p, isEV: v, depYr1: nextDepYr1, insurancePerYear: nextInsurance };
  }), []);

  /* Share URL — encodes current scenario into clipboard + URL hash */
  const shareUrl = () => {
    const encoded = encodeScenario({ ...d, _active: active });
    if (encoded) {
      const url = `${window.location.origin}${window.location.pathname}#s:${encoded}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      window.history.replaceState(null, "", `#s:${encoded}`);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 3000);
      track('share_scenario', { surface: 'calculator', state: d.state, isEV: !!d.isEV });
    }
  };

  /* Save current scenario */
  const saveScenario = (name) => {
    track('save_scenario', { surface: 'calculator', state: d.state, isEV: !!d.isEV });
    const newScenarios = [...scenarios, {
      /* crypto.randomUUID is well-supported in modern browsers (Chrome 92+,
         Firefox 95+, Safari 15.4+). Fall back to a Date.now()+random string
         on the rare older browser. */
      id: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      name: name || `Scenario ${scenarios.length + 1}`,
      data: d, active, savedAt: new Date().toLocaleDateString("en-AU"),
      /* O3: stamp the financial year and schema version so loadScenario can
         tell the user when a saved scenario was created under rates that
         have since been superseded. */
      fy: ONCOSTS_META.financialYear,
      schemaVersion: SCENARIO_SCHEMA_VERSION,
    }];
    setScenarios(newScenarios);
    try { localStorage.setItem("veercal_scenarios", JSON.stringify(newScenarios)); } catch {}
  };

  const deleteScenario = (id) => {
    const updated = scenarios.filter(s => s.id !== id);
    setScenarios(updated);
    try { localStorage.setItem("veercal_scenarios", JSON.stringify(updated)); } catch {}
  };

  const loadScenario = (scenario) => {
    track('load_scenario_saved', { surface: 'calculator' });
    setD(scenario.data);
    setActive(scenario.active || ["cash", "loan", "novated"]);
    setShowScenarios(false);
    /* O3: if the saved scenario predates the current financial year, surface
       the same versionNotice banner — the user gets the same explanation as
       loading an old shared URL. */
    if (scenario.fy && scenario.fy !== ONCOSTS_META.financialYear) {
      setVersionNotice(true);
    }
  };
  const allR = useMemo(() => ({
    cash: calcCash(d), loan: calcLoan(d, "loan"), dealer: calcLoan(d, "dealer"),
    lease: calcLease(d), novated: calcNovated(d),
  }), [d]);

  const results = active.map((m) => allR[m]);
  const sorted = [...results].sort((a, b) => a.totalCost - b.totalCost);

  const cd = useMemo(() => Array.from({ length: d.holdYears }, (_, i) => {
    const y = i + 1, row = { year: y };
    results.forEach((r) => {
      const yd = r.yearlyData[i];
      row[`${r.method}_t`] = yd?.totalSpent;
      row[`${r.method}_as`] = yd?.annualSpent;   /* annual (non-cumulative) spend */
      row[`${r.method}_a`] = yd?.assetValue;
      row[`${r.method}_n`] = yd?.netPosition;
      row[`${r.method}_m`] = yd?.monthlyEquivalent;
    });
    return row;
  }), [results, d.holdYears]);

  const toggle = (m) => setActive((p) => p.includes(m) ? p.length > 1 ? p.filter((x) => x !== m) : p : [...p, m]);
  /* Charts (DESIGN.md §5): grid lines in the line token, axis text in secondary
     text, the hero amber and the rest ink. */
  const looks = chartLooks(results, sorted[0].method);
  const cp = { stroke: tokensV2.line, vertical: false };
  const at = { fill: tokensV2.textSecondary, fontSize: 13, fontFamily: NUM };
  const endNames = (suffix) => Object.fromEntries(results.map((r) => [`${r.method}${suffix}`, { text: METHOD_META[r.method].label, hero: looks[r.method].hero }]));
  const LABEL_ROOM = 104;   /* right margin for the names at the line ends */

  const sideTabs = ["vehicle", "finance", "running", "novated", "extras"];
  const lctNow = calcLCT(baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d)), d.isEV);

  return (
    <ErrorBoundary>
      {/* Skip to main content — keyboard/screen reader navigation */}
      <a className="vc-skip" href="#main-content">Skip to main content</a>

      <SiteHeader current="calculator" />

      <main id="main-content">
        {/* O16: shared-URL version mismatch banner. Shown when a scenario was
            loaded from a hash whose schema version doesn't match the current
            SCENARIO_SCHEMA_VERSION. The link works — but the user should know
            the calculator's assumptions may have shifted since they got the
            link. Dismissable; doesn't persist across reloads. */}
        {versionNotice && (
          <div className="vc-notice no-print">
            <div className="vc-container vc-notice__inner">
              <p style={{ margin: 0, flex: "1 1 420px" }}>
                This shared link was created with an older Veercal version. The numbers shown use <strong>current {ONCOSTS_META.financialYear}</strong> rates and methodology — some figures may differ from when the link was originally shared.
              </p>
              <button type="button" className="vc-btn" onClick={() => setVersionNotice(false)}>
                Got it
              </button>
            </div>
          </div>
        )}

        {/* O15: "what's new" banner — shown until the user dismisses the
            newest CHANGES entry. Persisted per device via localStorage. */}
        {!changeBannerDismissed && CHANGES.length > 0 && (
          <div className="vc-notice no-print">
            <div className="vc-container vc-notice__inner">
              <p style={{ margin: 0, flex: "1 1 420px" }}>
                <strong>New ({CHANGES[0].date}):</strong> {CHANGES[0].title}.{" "}
                <button type="button" className="vc-link" onClick={() => setShowChanges(true)}>
                  See full change log →
                </button>
              </p>
              <button type="button" className="vc-btn" onClick={dismissChangeBanner}>
                Got it
              </button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <DisclaimerBanner />

        {/* ── Cookie / Privacy Notice ─────────────────────────────────────────── */}
        {!cookieConsent && (
          <div className="vc-notice vc-notice--ink no-print">
            <div className="vc-container vc-notice__inner">
              <p style={{ margin: 0, flex: "1 1 420px" }}>
                We use cookies and analytics to improve Veercal. By continuing to use this tool you accept our{" "}
                <a href="/privacy">Privacy Policy</a>.
              </p>
              <div className="vc-notice__actions">
                <button type="button" className="vc-btn vc-btn--accent" onClick={acceptCookies}>Accept</button>
                <button type="button" className="vc-btn vc-btn--on-ink" onClick={declineCookies}>Decline</button>
                <a href="/privacy">Learn more</a>
              </div>
            </div>
          </div>
        )}

        <div className="vc-container">
        <div className="vc-app-head">
          <h1 className="vc-h1">Car ownership cost calculator</h1>
          {/* Phones: the inputs are a drawer this button opens. */}
          <button type="button" ref={openInputsRef} className="vc-btn vc-app__open no-print"
            aria-expanded={sidebarOpen} aria-controls="calc-inputs" onClick={() => setSidebarOpen(o => !o)}>
            Edit inputs
          </button>
        </div>

        {/* ── Toolbar ────────────────────────────────────────────────────── */}
        <div className="vc-toolbar no-print">
          {/* Save scenario */}
          {!showSaveInput ? (
            <button type="button" className="vc-btn vc-btn--primary" onClick={() => { setSaveNameInput(`${fmt(d.vehiclePrice)} · ${d.holdYears}yr`); setShowSaveInput(true); }}>
              Save scenario
            </button>
          ) : (
            <div className="vc-toolbar__save">
              <div className="vc-input">
                <input
                  autoFocus
                  value={saveNameInput}
                  onChange={e => setSaveNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { saveScenario(saveNameInput); setShowSaveInput(false); } if (e.key === "Escape") setShowSaveInput(false); }}
                  placeholder="Scenario name…"
                  aria-label="Scenario name"
                />
              </div>
              <button type="button" className="vc-btn vc-btn--primary" onClick={() => { saveScenario(saveNameInput); setShowSaveInput(false); }}>
                Save
              </button>
              <button type="button" className="vc-close" aria-label="Close" onClick={() => setShowSaveInput(false)}>
                ✕
              </button>
            </div>
          )}

          {/* Load scenarios */}
          {scenarios.length > 0 && (
            <button type="button" className="vc-btn" aria-expanded={showScenarios} onClick={() => setShowScenarios(s => !s)}>
              Saved ({scenarios.length})
            </button>
          )}

          {/* Share link */}
          <button type="button" className="vc-btn" onClick={shareUrl}>
            {shareToast ? "Link copied!" : "Share"}
          </button>

          {/* Print / PDF */}
          <button type="button" className="vc-btn" onClick={() => { try { setTimeout(() => window.print(), 150); } catch(e) {} }}>
            Print / PDF
          </button>

          {/* Rates version badge */}
          <p className="vc-small vc-toolbar__end">
            Rates: FY2026–27 · Updated {ONCOSTS_META.lastReviewed}
          </p>
        </div>

        {/* ── Saved Scenarios Panel ────────────────────────────────────────── */}
        {showScenarios && scenarios.length > 0 && (
          <div className="no-print">
            <h2 className="vc-inputs__head">Saved Scenarios</h2>
            <ul className="vc-saved" style={{ marginTop: 12 }}>
              {scenarios.map(s => (
                <li key={s.id} className="vc-card vc-saved__item">
                  <div>
                    <div className="vc-saved__name">
                      {s.name}
                      {s.fy && s.fy !== ONCOSTS_META.financialYear && (
                        <span className="vc-chip vc-chip--info" title={`Saved with ${s.fy} rates — current is ${ONCOSTS_META.financialYear}`}>
                          {s.fy}
                        </span>
                      )}
                    </div>
                    <div className="vc-small">{s.savedAt} · {fmt(s.data.vehiclePrice)} · {s.data.holdYears}yr</div>
                  </div>
                  <button type="button" className="vc-btn vc-btn--primary" onClick={() => loadScenario(s)}>Load</button>
                  <button type="button" className="vc-close" aria-label="Delete" onClick={() => deleteScenario(s.id)}>✕</button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="vc-app">

          {/* Sidebar */}
          {/* Mobile overlay backdrop */}
          {sidebarOpen && <div className="vc-app__backdrop is-open" onClick={() => setSidebarOpen(false)} />}

          <div id="calc-inputs" className={cx("vc-card vc-app__inputs no-print", sidebarOpen && "is-open")}>
            {/* Mobile close button */}
            <button type="button" ref={closeInputsRef} className="vc-close vc-app__close" aria-label="Close" onClick={() => { setSidebarOpen(false); if (openInputsRef.current) openInputsRef.current.focus(); }}>✕</button>

            <SHead>Compare Methods</SHead>
            <ul className="vc-methods">
              {Object.keys(METHOD_META).map((m) => <MBadge key={m} method={m} active={active.includes(m)} onClick={() => toggle(m)} openModal={openModal} />)}
            </ul>

            <h2 className="vc-inputs__head" id="inputs-specifics">Adjust specifics</h2>
            <div className="vc-seg" role="group" aria-labelledby="inputs-specifics">
              {sideTabs.map((s) => (
                <button key={s} type="button" aria-pressed={st === s} onClick={() => setSt(s)}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {st === "vehicle" && <>
              <SHead>Vehicle</SHead>
              <NI label="Vehicle Price (drive-away)" value={d.vehiclePrice} onChange={(v) => upd("vehiclePrice", v)} info="depreciation" openModal={openModal} />
              {/* Live drive-away breakdown — shows what's hidden inside the dealer's
                  advertised price (matches the panel shown on /compare and /stamp-duty). */}
              {(() => {
                const _b   = baseFromDriveAway(d.vehiclePrice, d.state, d.isEV, sdVType(d));
                const _lct = Math.round(calcLCT(_b, d.isEV));
                const _sd  = Math.round(calcSD(_b + _lct, d.state, sdVType(d)));
                return (
                  <div className="vc-aside">
                    <p className="vc-label" style={{ margin: 0 }}>Inside your drive-away ({d.state})</p>
                    <Rows>
                      <DRow label="Estimated base" value={fmt(_b)} />
                      <DRow label="Stamp duty (included)" value={fmt(_sd)} />
                      {_lct > 0 && <DRow label="LCT (included)" value={fmt(_lct)} />}
                    </Rows>
                  </div>
                );
              })()}
              <Slider label="Hold Period" value={d.holdYears} onChange={(v) => upd("holdYears", v)} min={1} max={10} fmt={(v) => `${v} years`} />
              <Slider label="Annual KM" value={d.annualKm} onChange={(v) => upd("annualKm", v)} min={5000} max={50000} step={1000} fmt={(v) => `${v.toLocaleString()} km`} />
              {/* O7: km band notice — the ATO statutory residual depends on the
                  km band (15k / 25k / 35k / 45k / >45k). Show the band the
                  user's in, the resulting residual for their lease term, and a
                  Reset button if they've overridden it. */}
              {(() => {
                const km = d.annualKm;
                const bandLabel = km <= 15000 ? "≤ 15,000 km/yr"
                  : km <= 25000 ? "15,001–25,000 km/yr"
                  : km <= 35000 ? "25,001–35,000 km/yr"
                  : km <= 45000 ? "35,001–45,000 km/yr"
                  : "over 45,000 km/yr";
                const atoPct = atoResidual(km, d.novatedTermYears);
                const diverged = Math.abs(d.novatedResidualPct - atoPct) > 0.005;
                return (
                  <div className="vc-aside">
                    <Rows>
                      <DRow label="ATO km band" value={bandLabel} />
                      <DRow label={`Residual for ${d.novatedTermYears}-yr lease`} value={fmtP(atoPct)} />
                    </Rows>
                    {diverged && (
                      <button type="button" className="vc-btn" onClick={() => upd("novatedResidualPct", atoPct)}>
                        Reset residual to ATO band ({fmtP(atoPct)})
                      </button>
                    )}
                  </div>
                );
              })()}
              <SHead info="depreciation" openModal={openModal}>Depreciation</SHead>
              <Slider label="Year 1 drop" info="depreciation" openModal={openModal} value={d.depYr1} onChange={(v) => upd("depYr1", v)} min={0.05} max={0.35} step={0.01} fmt={fmtP} />
              <Slider label="Year 2 drop" value={d.depYr2} onChange={(v) => upd("depYr2", v)} min={0.05} max={0.25} step={0.01} fmt={fmtP} />
              <Slider label="Year 3+ drop" value={d.depYrN} onChange={(v) => upd("depYrN", v)} min={0.03} max={0.20} step={0.01} fmt={fmtP} />
            </>}

            {st === "finance" && <>
              <SHead>Cash / Opportunity Cost</SHead>
              <Slider label="Investment return rate" value={d.opportunityCostPct} info="opportunityCost" openModal={openModal} onChange={(v) => upd("opportunityCostPct", v)} min={0.01} max={0.12} step={0.005} fmt={fmtP} sub="What lump sum cash could earn" />
              <SHead info="personalLoan" openModal={openModal}>Personal Loan</SHead>
              <NI label="Deposit" value={d.loanDeposit} onChange={(v) => upd("loanDeposit", v)} />
              <Slider isRate={true} label="Interest rate" value={d.loanRate} onChange={(v) => upd("loanRate", v)} min={0.04} max={0.20} step={0.005} fmt={fmtP} />
              <Slider label="Term" value={d.loanTermYears} onChange={(v) => upd("loanTermYears", v)} min={1} max={7} fmt={(v) => `${v} yrs`} />
              <SHead info="dealerFinance" openModal={openModal}>Dealer Finance</SHead>
              <NI label="Deposit" value={d.dealerDeposit} onChange={(v) => upd("dealerDeposit", v)} />
              <Slider isRate={true} label="Interest rate" value={d.dealerRate} onChange={(v) => upd("dealerRate", v)} min={0.02} max={0.15} step={0.005} fmt={fmtP} />
              <Slider label="Term" value={d.dealerTermYears} onChange={(v) => upd("dealerTermYears", v)} min={1} max={7} fmt={(v) => `${v} yrs`} />
              <Slider label="Balloon %" value={d.balloonPct} info="balloonPayment" openModal={openModal} onChange={(v) => upd("balloonPct", v)} min={0} max={0.40} step={0.01} fmt={fmtP} />
              <SHead info="financeLease" openModal={openModal}>Finance Lease</SHead>
              <Slider isRate={true} label="Rate" value={d.leaseRate} onChange={(v) => upd("leaseRate", v)} min={0.03} max={0.12} step={0.005} fmt={fmtP} />
              <Slider label="Term" value={d.leaseTermYears} onChange={(v) => upd("leaseTermYears", v)} min={1} max={5} fmt={(v) => `${v} yrs`} />
              <Slider label="Residual %" value={d.leaseResidualPct} info="residualATO" openModal={openModal} onChange={(v) => upd("leaseResidualPct", v)} min={0.10} max={0.80} step={0.01} fmt={fmtP} sub="ATO statutory by term + km" />
              <SHead>Refinance Modelling</SHead>
              <Slider label="Refinance at year" value={d.refinanceYear} onChange={(v) => upd("refinanceYear", v)} min={0} max={6} fmt={(v) => v === 0 ? "Off" : `Year ${v}`} />
              {d.refinanceYear > 0 && <Slider label="New rate after refi" value={d.refinanceRate} onChange={(v) => upd("refinanceRate", v)} min={0.03} max={0.15} step={0.005} fmt={fmtP} />}
            </>}

            {st === "running" && <>
              <SHead>Running Costs</SHead>
              <Toggle label="Electric Vehicle (EV)" value={d.isEV} onChange={setIsEV} sub="Switches fuel → charging" />
              {d.isEV ? <>
                <Slider label="Home charge ($/kWh)" value={d.evHomeChargeKwh} onChange={(v) => upd("evHomeChargeKwh", v)} min={0.15} max={0.45} step={0.01} fmt={(v) => `$${v.toFixed(2)}`} />
                <Slider label="Public DC ($/kWh)" value={d.evPublicChargeKwh} onChange={(v) => upd("evPublicChargeKwh", v)} min={0.30} max={1.20} step={0.05} fmt={(v) => `$${v.toFixed(2)}`} sub="Fast-charging rate at public stations" />
                <Slider label="Home charging %" value={d.evPctHomeCharge} onChange={(v) => upd("evPctHomeCharge", v)} min={0} max={1} step={0.05} fmt={(v) => `${Math.round(v * 100)}%`} sub="Share of km charged at home (rest at public DC)" />
                <Slider label="Efficiency (kWh/100km)" value={d.evEfficiencyKwh} onChange={(v) => upd("evEfficiencyKwh", v)} min={12} max={28} step={0.5} fmt={(v) => `${v} kWh`} />
              </> : <>
                <Slider label="Fuel price" value={d.fuelPerL} onChange={(v) => upd("fuelPerL", v)} min={1.50} max={2.80} step={0.05} fmt={(v) => `$${v.toFixed(2)}/L`} />
                <Slider label="Fuel efficiency" value={d.fuelL100km} onChange={(v) => upd("fuelL100km", v)} min={4} max={18} step={0.5} fmt={(v) => `${v}L/100km`} />
              </>}
              <NI label="Tyres / year" value={d.tyresPerYear} onChange={(v) => upd("tyresPerYear", v)} />
              <NI label="Servicing / year" value={d.servicesPerYear} onChange={(v) => upd("servicesPerYear", v)} />
              <NI label="Insurance / year" value={d.insurancePerYear} onChange={(v) => upd("insurancePerYear", v)} />
              <NI label="Registration / year" value={d.regPerYear} onChange={(v) => upd("regPerYear", v)} />
              <div className="vc-reading vc-reading--strong">
                <div className="vc-reading__body">
                  <div className="vc-reading__label">Total Running / yr</div>
                  <div className="vc-reading__value">{fmt(running(d))}</div>
                  <div className="vc-reading__note">{fmt(Math.round(running(d) / 12))} / month</div>
                </div>
              </div>
            </>}

            {st === "novated" && <>
              <SHead info="novatedLease" openModal={openModal}>Novated Lease</SHead>
              <NI label="Gross Annual Salary" value={d.grossSalary} info="fbtSalary" openModal={openModal} onChange={(v) => upd("grossSalary", v)} />
              <div className="vc-aside">
                <Rows>
                  <DRow label="Marginal tax" value={fmtP(mtr(d.grossSalary))} />
                  <DRow label="Medicare levy" value={fmtP(med(d.grossSalary))} />
                  <DRow label="Combined rate" value={fmtP(mtr(d.grossSalary) + med(d.grossSalary))} bold />
                </Rows>
              </div>
              <Slider isRate={true} label="Rate" value={d.novatedRate} onChange={(v) => upd("novatedRate", v)} min={0.03} max={0.12} step={0.005} fmt={fmtP} />
              <Slider label="Term" value={d.novatedTermYears} onChange={(v) => upd("novatedTermYears", v)} min={1} max={5} fmt={(v) => `${v} yrs`} />
              <NI label="Provider management fee / yr" value={d.novatedProviderFeeAnnual} onChange={(v) => upd("novatedProviderFeeAnnual", v)} sub="Typical retail range $300–$800/yr; set to $0 if your employer absorbs it" />
              <p className="vc-small" style={{ margin: 0 }}>
                ATO minimum for {d.annualKm.toLocaleString()} km/yr · {d.novatedTermYears}-yr lease:{" "}
                <strong>{fmtP(atoResidual(d.annualKm, d.novatedTermYears))}</strong>
              </p>
              {Math.abs(d.novatedResidualPct - atoResidual(d.annualKm, d.novatedTermYears)) > 0.005 && (
                <button type="button" className="vc-btn" style={{ alignSelf: "flex-start" }} onClick={() => upd("novatedResidualPct", atoResidual(d.annualKm, d.novatedTermYears))}>
                  Reset to ATO
                </button>
              )}
              <Slider label="Residual %" value={d.novatedResidualPct} info="residualATO" openModal={openModal} onChange={(v) => upd("novatedResidualPct", v)} min={0.10} max={0.80} step={0.01} fmt={fmtP} />
              <Toggle label="Provider passes on GST credit on purchase" value={d.novatedGstOnPurchase} onChange={(v) => upd("novatedGstOnPurchase", v)} sub="Most providers finance the car ex-GST — the financier claims the GST input credit on the purchase (capped at $6,353). Turn off if your employer/provider doesn't pass this on." />
              {isEvFbtExempt(d.vehiclePrice, sdVType(d), d.state) && (
                <span className="vc-chip vc-chip--good" style={{ alignSelf: "flex-start" }}>
                  EV FBT Exemption — waived
                </span>
              )}
            </>}

            {st === "extras" && <>
              <SHead info="stampDuty" openModal={openModal}>State / Stamp Duty</SHead>
              <Sel label="State" value={d.state} onChange={(v) => upd("state", v)} options={["VIC","NSW","QLD","WA","SA","TAS","ACT","NT"]} />
              <KPI label={`Stamp Duty (${d.state})`} value={fmt(stampDutyForDisplay(d))} />
              <Toggle label="Finance stamp duty" value={d.financeStampDuty} onChange={(v) => upd("financeStampDuty", v)} sub="Roll SD into the loan instead of paying upfront. Increases monthly payment + total interest; reduces day-1 cash needed." />
              <SHead info="lct" openModal={openModal}>Luxury Car Tax</SHead>
              <KPI label="LCT (extracted from your drive-away price)" value={lctNow > 0 ? fmt(lctNow) : "Nil — under LCT threshold"} />
              <SHead>EV Benefits</SHead>
              <Toggle label="Electric Vehicle" value={d.isEV} onChange={setIsEV} />
              {d.isEV && (
                <div className="vc-field">
                  <span className="vc-field__label" id="ev-type-label">EV Type</span>
                  <div className="vc-seg" role="group" aria-labelledby="ev-type-label">
                    {[{ v: "bev", l: "Battery EV (BEV)" }, { v: "phev", l: "Plug-in Hybrid (PHEV)" }].map(opt => (
                      <button key={opt.v} type="button" aria-pressed={d.evType === opt.v} onClick={() => upd("evType", opt.v)}>{opt.l}</button>
                    ))}
                  </div>
                  {d.evType === "phev" && (
                    <div className="vc-aside">
                      <p className="vc-aside__text">
                        PHEV FBT exemption ended for <strong>new agreements</strong> from 1 April 2025. Existing PHEV novated leases in use before that date stay exempt until the lease ends, unless it's changed or extended. This calculator models new arrangements — set type back to BEV or ICE to see your scenario.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {d.isEV && <NI label="Govt Rebate / Subsidy" value={d.evGovtRebate} onChange={(v) => upd("evGovtRebate", v)} sub="VIC $3k · NSW $3k · ACT $15k" />}
            </>}
          </div>

          {/* Main */}
          <div className="vc-app__results">
            <Tabs tabs={[
              { id: "results", label: "Results"  },
              { id: "exit",    label: "Exit Sim" },
            ]} active={tab} onChange={setTab} />

            {/* The depth control is a direct child of this tall panel, so it can
                stay at the top of the screen on phones as the results scroll. */}
            <div id="tab-panel" role="tabpanel" aria-labelledby={`tab-${tab}`} className="vc-app__panel">

            {/* RESULTS — bento layout + progressive depth */}
            {tab === "results" && (() => {
              const w = sorted[0];
              const runnerUp = sorted.length > 1 ? sorted[1] : null;
              const heroSummary = {
                title: METHOD_META[w.method].label,
                total: w.totalCost,
                monthly: w.effectiveMonthly,
                savingVs: runnerUp
                  ? { amount: runnerUp.totalCost - w.totalCost, label: METHOD_META[runnerUp.method].label }
                  : null,
              };
              const verdict = (() => {
                const winner = sorted[0], loser = sorted[sorted.length - 1];
                const saving = loser.totalCost - winner.totalCost;
                const intDiff = (loser.totalInterest || 0) - (winner.totalInterest || 0);
                const reason = winner.method === "novated"
                  ? `pre-tax salary sacrifice saves ${fmt(winner.taxSavingMonthly)}/mo in income tax${winner.fbtExempt ? " and FBT is waived (EV)" : ""}`
                  : winner.method === "cash"
                  ? `no interest cost, though opportunity cost of ${fmt(winner.opportunityCostTotal)} is included`
                  : intDiff > 0
                  ? `lower interest cost — saves ${fmt(intDiff)} vs ${METHOD_META[loser.method].label}`
                  : `lower total outlay over the hold period`;
                return `Modelled saving of ${fmt(saving)} vs ${METHOD_META[loser.method].label} — ${reason}. Figures are estimates only.`;
              })();
              const checks = checkRows(allR, active, d);
              const tiles = [
                { key: "compare", kind: "custom", node: (
                  <div className="vc-calc__block">
                    <h2 className="vc-h3">True total cost — all options</h2>
                    <CostTubes sorted={sorted} label="True total cost — all options" />
                    <div className="vc-options">
                      {sorted.map((r, i) => <SCard key={r.method} result={r} rank={i} openModal={openModal} />)}
                    </div>
                  </div>
                ) },
                checks.length > 0 && { key: "checks", kind: "custom", node: <ChecksBand groups={checks} /> },
                { key: "effmo", kind: "metric", label: "Effective / mo", value: fmt(w.effectiveMonthly) },
                { key: "runyr", kind: "metric", label: "Running / yr", value: fmt(running(d)) },
                { key: "exitval", kind: "metric", label: "Exit value", value: w.exitValue ? fmt(w.exitValue) : "N/A" },
                { key: "stampduty", kind: "metric", label: "Stamp duty", value: fmt(w.stampDuty) },
                { key: "cumspend", kind: "chart", title: "Cumulative spend over time", node: (
                  <div ref={chartRefCumSpend}>
                    <ResponsiveContainer width="100%" height={230}>
                      <AreaChart data={cd} margin={{ top: 8, right: LABEL_ROOM, bottom: 0, left: 8 }}>
                        <CartesianGrid {...cp} />
                        <XAxis dataKey="year" tick={at} stroke={tokensV2.lineStrong} />
                        <YAxis tickFormatter={fmtK} tick={at} stroke={tokensV2.lineStrong} />
                        <Tooltip content={<TT />} />
                        {results.map((r) => {
                          const k = looks[r.method];
                          return (
                            <Area key={r.method} type="monotone" dataKey={`${r.method}_t`} name={METHOD_META[r.method].label}
                              stroke={k.stroke} strokeWidth={k.width} strokeDasharray={k.dash}
                              fill={k.hero ? tokensV2.amber : "none"} fillOpacity={k.hero ? 0.14 : 0}
                              dot={false} isAnimationActive={false} />
                          );
                        })}
                        <Customized component={<EndLabels names={endNames("_t")} />} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) },
                { key: "why", kind: "custom", node: (
                  <div className="vc-calc__block">
                    <h2 className="vc-h3">Why this wins</h2>
                    <p>{verdict}</p>
                  </div>
                ) },
              ].filter(Boolean);
              return (
                <>
                  {/* Headline context strip */}
                  <div className="vc-calc__block" style={{ gap: 4 }}>
                    <p className="vc-label">True total cost of owning your {fmt(d.vehiclePrice)} car</p>
                    <p>
                      {d.holdYears}-year hold · {d.state} · {d.annualKm.toLocaleString()} km/yr · includes all finance, on-costs, running costs, and exit value.
                    </p>
                  </div>
                  <DepthControl depth={depth} onChange={setDepth} />
                  {/* aria-live announces the new winner + total when inputs change. */}
                  <div aria-live="polite" aria-atomic="true">
                    <BentoResults
                      depth={depth}
                      bareDetail
                      hero={{ items: buildCostComparison(w, d, d), summary: heroSummary, onDrill: () => setDepth(2) }}
                      tiles={tiles}
                    />
                  </div>
                </>
              );
            })()}

            {/* DEPTH 3 — full analytical detail (Total Cost + Sensitivity;
                Cash Flow / Equity / Deep Dive / On-Costs follow below). */}
            {tab === "results" && depth === 3 && <>
              <Panel>
                <ChartHeader title="Total Cost Comparison" sub={`Finance + running − exit value over ${d.holdYears} years`} infoId="chartTotalCost" chartRef={chartRefTotalCost} downloadName="veercal-total-cost-comparison" openModal={openModal} />
                <div ref={chartRefTotalCost} className="vc-chart__plot">
                <ResponsiveContainer width="100%" height={Math.max(150, 44 * results.length + 30)}>
                  <BarChart data={results.map((r) => ({ name: METHOD_META[r.method].label, method: r.method, v: r.totalCost }))} layout="vertical" margin={{ top: 4, right: 72, bottom: 0, left: 8 }}>
                    <CartesianGrid stroke={tokensV2.line} horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtK} tick={at} stroke={tokensV2.lineStrong} />
                    <YAxis type="category" dataKey="name" tick={{ ...at, fill: tokensV2.ink }} width={112} stroke={tokensV2.lineStrong} />
                    <Tooltip formatter={(v) => fmt(v)} {...TIP_STYLE} />
                    <Bar dataKey="v" name="True total cost" radius={[0, 2, 2, 0]} isAnimationActive={false}>
                      {results.map((r) => <Cell key={r.method} fill={looks[r.method].hero ? tokensV2.amber : tokensV2.ink} />)}
                      <LabelList dataKey="v" position="right" formatter={(v) => fmt(v)} style={{ fill: tokensV2.ink, fontSize: 13, fontFamily: NUM, fontWeight: tokensV2.fw.label }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </Panel>

              <SensitivityPanel d={d} winner={sorted[0]} openModal={openModal} />
            </>}

            {/* CASHFLOW (depth-3 reveal) */}
            {tab === "results" && depth === 3 && <>
              <Panel>
                <ChartHeader title="Annual Cash Outflow" sub={`Actual spend each year — finance payments + running costs (not cumulative)`} infoId="chartAnnualOutflow" chartRef={chartRefAnnualOutflow} downloadName="veercal-annual-cash-outflow" openModal={openModal} />
                <div ref={chartRefAnnualOutflow} className="vc-chart__plot">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={cd} margin={{ top: 5, right: 8, bottom: 0, left: 8 }}>
                    <defs><HatchDef /></defs>
                    <CartesianGrid {...cp} />
                    <XAxis dataKey="year" tick={at} stroke={tokensV2.lineStrong} />
                    <YAxis tickFormatter={fmtK} tick={at} stroke={tokensV2.lineStrong} />
                    <Tooltip content={<TT />} cursor={{ fill: tokensV2.tint }} />
                    {results.map((r) => <Bar key={r.method} dataKey={`${r.method}_as`} name={METHOD_META[r.method].label} {...looks[r.method].bar} radius={[2, 2, 0, 0]} isAnimationActive={false} />)}
                  </BarChart>
                </ResponsiveContainer>
                </div>
                <ChartKey results={results} looks={looks} kind="bar" />
              </Panel>

              <Panel>
                <ChartHeader title="Effective Monthly Cost" sub={`Payment + running ÷ 12 · cash amortises purchase price`} infoId="chartEffectiveMonthly" chartRef={chartRefEffMonthly} downloadName="veercal-effective-monthly" openModal={openModal} />
                <div ref={chartRefEffMonthly} className="vc-chart__plot">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={cd} margin={{ top: 8, right: LABEL_ROOM, bottom: 0, left: 8 }}>
                    <CartesianGrid {...cp} />
                    <XAxis dataKey="year" tick={at} stroke={tokensV2.lineStrong} />
                    <YAxis tickFormatter={(v) => `$${v.toLocaleString()}`} tick={at} stroke={tokensV2.lineStrong} />
                    <Tooltip content={<TT />} />
                    {results.map((r) => {
                      const k = looks[r.method];
                      return (
                        <Line key={r.method} type="monotone" dataKey={`${r.method}_m`} name={METHOD_META[r.method].label}
                          stroke={k.stroke} strokeWidth={k.width} strokeDasharray={k.dash}
                          dot={{ r: 3, fill: k.stroke, strokeWidth: 0 }} isAnimationActive={false} />
                      );
                    })}
                    <Customized component={<EndLabels names={endNames("_m")} />} />
                  </LineChart>
                </ResponsiveContainer>
                </div>
              </Panel>

              {active.includes("novated") && (
                <Panel>
                  <h2 className="vc-chart__title">Novated Salary Sacrifice Breakdown</h2>
                  <div className="vc-readings vc-readings--three">
                    {[
                      { l: "Gross salary sacrifice/mo", v: fmt(allR.novated.grossMonthly) },
                      { l: "Tax saving/mo", v: fmt(allR.novated.taxSavingMonthly) },
                      { l: "FBT contribution/mo", v: allR.novated.fbtExempt ? "Exempt (EV)" : fmt(allR.novated.fbtMonthly) },
                      { l: "GST input credit/mo", v: fmt(allR.novated.gstCreditMonthly) },
                      { l: "Net out-of-pocket/mo", v: fmt(allR.novated.monthlyPayment) },
                      { l: "Annual tax saving", v: fmt(allR.novated.taxSavingMonthly * 12) },
                      { l: "Residual buyout", v: fmt(allR.novated.residualBuyout) },
                    ].map((x) => <KPI key={x.l} label={x.l} value={x.v} />)}
                  </div>
                </Panel>
              )}

              {d.refinanceYear > 0 && (
                <Panel>
                  <h2 className="vc-chart__title">Refinance at Year {d.refinanceYear} → {fmtP(d.refinanceRate)}</h2>
                  <div className="vc-options">
                    {["loan", "dealer"].filter((m) => active.includes(m)).map((m) => {
                      const r = allR[m];
                      const termYrs = m === "dealer" ? d.dealerTermYears : d.loanTermYears;
                      return (
                        <div key={m}>
                          <h3 className="vc-checks__option">{METHOD_META[m].label}</h3>
                          <Rows>
                            <DRow label="Total interest (with refi)" value={fmt(r.totalInterest)} />
                            <DRow label={`Monthly (Y1–${d.refinanceYear})`} value={fmt(r.monthlyPayment)} />
                            {r.monthlyPaymentRefi != null && (
                              <DRow label={`Monthly (Y${d.refinanceYear + 1}–${termYrs})`} value={fmt(r.monthlyPaymentRefi)} />
                            )}
                          </Rows>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              )}
            </>}

            {/* EQUITY (depth-3 reveal) */}
            {tab === "results" && depth === 3 && <>
              <Panel>
                <ChartHeader title="Asset Value vs Total Spent" sub={`Solid = vehicle value · Dashed = cumulative spend · Gap = true net cost`} infoId="chartAssetVsSpend" chartRef={chartRefAssetSpend} downloadName="veercal-asset-vs-spend" openModal={openModal} />
                <div ref={chartRefAssetSpend} className="vc-chart__plot">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={cd} margin={{ top: 5, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid {...cp} />
                    <XAxis dataKey="year" tick={at} stroke={tokensV2.lineStrong} />
                    <YAxis tickFormatter={fmtK} tick={at} stroke={tokensV2.lineStrong} />
                    <Tooltip content={<TT />} />
                    {results.map((r) => {
                      const k = looks[r.method];
                      const dot = k.hero ? false : markerDot(k.marker);
                      return [
                        <Line key={`${r.method}_av`} type="monotone" dataKey={`${r.method}_a`} name={`${METHOD_META[r.method].label} (asset)`} stroke={k.stroke} strokeWidth={k.width} dot={dot} isAnimationActive={false} />,
                        <Line key={`${r.method}_tv`} type="monotone" dataKey={`${r.method}_t`} name={`${METHOD_META[r.method].label} (spent)`} stroke={k.stroke} strokeWidth={k.width} strokeDasharray="6 4" dot={dot} isAnimationActive={false} />,
                      ];
                    })}
                  </LineChart>
                </ResponsiveContainer>
                </div>
                <ChartKey results={results} looks={looks} kind="line" />
              </Panel>

              <Panel>
                <ChartHeader title="Net Wealth Position" sub={`Asset value minus all costs. Below zero = net financial loss on vehicle.`} infoId="chartNetWealth" chartRef={chartRefNetWealth} downloadName="veercal-net-wealth" openModal={openModal} />
                <div ref={chartRefNetWealth} className="vc-chart__plot">
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={cd} margin={{ top: 8, right: LABEL_ROOM, bottom: 0, left: 8 }}>
                    <CartesianGrid {...cp} />
                    <XAxis dataKey="year" tick={at} stroke={tokensV2.lineStrong} />
                    <YAxis tickFormatter={fmtK} tick={at} stroke={tokensV2.lineStrong} />
                    <ReferenceLine y={0} stroke={tokensV2.lineStrong} strokeWidth={2} />
                    <Tooltip content={<TT />} />
                    {results.map((r) => {
                      const k = looks[r.method];
                      return (
                        <Area key={r.method} type="monotone" dataKey={`${r.method}_n`} name={METHOD_META[r.method].label}
                          stroke={k.stroke} strokeWidth={k.width} strokeDasharray={k.dash}
                          fill={k.hero ? tokensV2.amber : "none"} fillOpacity={k.hero ? 0.14 : 0}
                          dot={false} isAnimationActive={false} />
                      );
                    })}
                    <Customized component={<EndLabels names={endNames("_n")} />} />
                  </AreaChart>
                </ResponsiveContainer>
                </div>
              </Panel>
            </>}

            {/* EXIT */}
            {tab === "exit" && (
              <Panel>
                <h2 className="vc-chart__title">Exit Simulator</h2>
                <p className="vc-chart__sub">True cost position — including cost per km — if you sell or return at any year.</p>
                <ExitSim results={results} d={d} />
              </Panel>
            )}

            {/* BREAKDOWN — year-by-year (depth-3 reveal) */}
            {tab === "results" && depth === 3 && results.map((r) => {
              const m = METHOD_META[r.method];
              return (
                <Panel key={r.method}>
                  <h2 className="vc-chart__title" id={`yby-${r.method}`}>{m.label} — Year-by-Year</h2>
                  <div className="vc-readings vc-readings--three">
                    {[
                      { l: "Upfront Cost", v: fmt(r.upfrontCost) },
                      { l: "Monthly Payment", v: r.monthlyPayment ? fmt(r.monthlyPayment) : "None" },
                      { l: "Total Interest", v: r.totalInterest ? fmt(r.totalInterest) : "$0" },
                      { l: "Running Total", v: fmt(running(d) * d.holdYears) },
                      { l: "Exit Value", v: r.exitValue ? fmt(r.exitValue) : "N/A" },
                      { l: "True Net Cost", v: fmt(r.totalCost) },
                    ].map((x) => <KPI key={x.l} label={x.l} value={x.v} />)}
                  </div>
                  <div className="vc-table-wrap" tabIndex={0} role="region" aria-labelledby={`yby-${r.method}`}>
                    <table className="vc-table">
                      <thead>
                        <tr>
                          {[
                            {
                              h: "Year",
                              tip: "The year of ownership. Year 1 is the first 12 months after purchase.",
                            },
                            {
                              h: "Asset Value",
                              tip: "Estimated market value of the vehicle at the end of this year, based on your depreciation settings. Year 1 typically drops 15%, then 12%, then 10%/yr — you can adjust these in the Vehicle tab.",
                            },
                            {
                              h: "Total Spent",
                              tip: "Cumulative cash outflow from day one to the end of this year. Includes upfront costs (deposit, stamp duty), all finance repayments, and all running costs (fuel, insurance, rego, servicing). Does not subtract the asset value.",
                            },
                            {
                              h: "Monthly Equiv",
                              tip: "Total spent so far divided by the number of months elapsed. This is the average monthly cash outflow to date — useful for comparing structures at the same point in time.",
                            },
                            {
                              h: "Equity / Net",
                              tip: "Asset value minus total spent. A positive number means the car is still worth more than you have paid out so far — you are in a net positive position. A negative number means your cumulative spend has exceeded the car's remaining value. For financed structures this typically turns negative around year 2–3 as interest and running costs accumulate.",
                            },
                            {
                              h: "Cost/km",
                              tip: "Net cost per kilometre driven: (total spent − asset value) ÷ km driven so far.\n\nThis can be NEGATIVE in early years — particularly for financed structures — because the car still holds more value than you have paid out. A negative cost/km does not mean you are making money; it means your net position is still positive at this point.\n\nAs depreciation accumulates and total spend grows, this figure rises and eventually turns positive — settling into the true long-run cost per km. By the end of your hold period this number is one of the most useful comparisons across finance structures.",
                            },
                          ].map(({ h, tip }) => (
                            <th key={h} scope="col">
                              <span title={tip} style={{ cursor: "help", textDecoration: "underline dotted", textUnderlineOffset: 3, whiteSpace: "nowrap" }}>{h}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {r.yearlyData.map((yd, i) => {
                          const cpk = ((yd.totalSpent - yd.assetValue) / ((i + 1) * d.annualKm)).toFixed(2);
                          const eq = yd.equity !== undefined ? yd.equity : yd.netPosition;
                          return (
                            <tr key={yd.year}>
                              <th scope="row">{yd.year}</th>
                              <td>{fmt(yd.assetValue)}</td>
                              <td>{fmt(yd.totalSpent)}</td>
                              <td>{fmt(yd.monthlyEquivalent)}</td>
                              <td>{fmt(eq)}</td>
                              <td>
                                <span title={parseFloat(cpk) < 0 ? "Negative cost/km: your net position is still positive — the car is worth more than you have paid out so far. Hover the column header for a full explanation." : undefined} style={{ cursor: parseFloat(cpk) < 0 ? "help" : "default" }}>
                                  {parseFloat(cpk) < 0 ? `${cpk}/km` : `$${cpk}/km`}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              );
            })}

            {/* ON-COSTS (depth-3 reveal) */}
            {tab === "results" && depth === 3 && <>
              <Panel>
                <h2 className="vc-chart__title">Government Charges & On-Costs</h2>
                <p className="vc-chart__sub">Stamp duty · Luxury Car Tax · EV rebates · FY2026–27 rates</p>
                <div className="vc-readings">
                  <KPI label={`Stamp Duty (${d.state})`} value={fmt(stampDutyForDisplay(d))} />
                  <KPI label="LCT Payable" value={lctNow > 0 ? fmt(lctNow) : "None"} />
                  <KPI label="EV Rebate" value={d.isEV && d.evGovtRebate > 0 ? `-${fmt(d.evGovtRebate)}` : "None"} />
                  <KPI label="Total On-Costs" value={fmt(stampDutyForDisplay(d) + lctNow - (d.isEV ? d.evGovtRebate : 0))} />
                </div>
              </Panel>

              <Panel>
                <h2 className="vc-chart__title">Stamp Duty by State — {fmt(d.vehiclePrice)} vehicle</h2>
                <div className="vc-chart__plot">
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={["VIC","NSW","QLD","WA","SA","TAS","ACT","NT"].map((s) => { const _vt = sdVType(d); const _b = baseFromDriveAway(d.vehiclePrice, s, d.isEV, _vt); return { s, v: Math.round(calcSD(_b + calcLCT(_b, d.isEV), s, _vt)) }; })}
                    margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid {...cp} />
                    <XAxis dataKey="s" tick={at} stroke={tokensV2.lineStrong} />
                    <YAxis tickFormatter={fmtK} tick={at} stroke={tokensV2.lineStrong} />
                    <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => `${l}`} {...TIP_STYLE} />
                    <Bar dataKey="v" name="Stamp Duty" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                      {["VIC","NSW","QLD","WA","SA","TAS","ACT","NT"].map((s) => (
                        <Cell key={s} fill={s === d.state ? tokensV2.amber : tokensV2.ink} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </Panel>

              <Panel>
                <h2 className="vc-chart__title">LCT Guide — FY2026–27</h2>
                <div className="vc-readings">
                  <KPI label="Standard threshold" value={fmt(LCT_STD)} sub="Petrol / diesel" />
                  <KPI label="Fuel-efficient threshold" value={fmt(LCT_FE)} sub="EV, PHEV, hybrid ≤7L/100km" />
                  <KPI label="LCT rate" value="33%" sub="On excess ÷ 1.1" />
                  <KPI label="Your LCT" value={lctNow > 0 ? fmt(lctNow) : "Nil — under threshold"} />
                </div>
              </Panel>

              {d.isEV && (() => {
                const _fbtExempt = isEvFbtExempt(d.vehiclePrice, sdVType(d), d.state);
                return (
                  <Panel>
                    <h2 className="vc-chart__title">EV Benefits Summary</h2>
                    <div className="vc-readings">
                      <KPI label="Fuel saving vs ICE/yr" sub="Based on your home/public charging mix"
                        value={(() => {
                          const ice = (d.annualKm / 100) * d.fuelL100km * d.fuelPerL;
                          const totalKwh = (d.annualKm / 100) * d.evEfficiencyKwh;
                          const homePct = d.evPctHomeCharge != null ? d.evPctHomeCharge : 1;
                          const publicRate = d.evPublicChargeKwh != null ? d.evPublicChargeKwh : d.evHomeChargeKwh;
                          const ev = totalKwh * homePct * d.evHomeChargeKwh + totalKwh * (1 - homePct) * publicRate;
                          return fmt(Math.round(ice - ev));
                        })()} />
                      <KPI label="Service saving/yr" value={fmt(Math.round(d.servicesPerYear * 0.4))} sub="~40% lower for EVs" />
                      <KPI label="FBT exemption" value={_fbtExempt ? "Eligible" : "Over threshold"} />
                      <KPI label="Govt rebate" value={d.evGovtRebate > 0 ? fmt(d.evGovtRebate) : "Set in Extras"} />
                    </div>
                  </Panel>
                );
              })()}
            </>}

            {/* Footer */}
            <div className="vc-fine vc-fine--rule" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span>Rates: FY2026–27 · Verified {ONCOSTS_META.lastReviewed} · Next review {ONCOSTS_META.nextReviewDue}</span>
                <span style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                  <button type="button" className="vc-link" onClick={() => setShowChanges(true)}>What's changed</button>
                  <a href="/learn">Learn</a>
                  <a href="/terms">Terms of Use</a>
                  <a href="/privacy">Privacy Policy</a>
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                <span>© {new Date().getFullYear()} Veercal. All rights reserved. · <a href="/learn">Learn</a></span>
                <span>General information only — not financial advice. <a href="/learn/how-novated-leases-work">Learn more →</a></span>
              </div>
            </div>
            </div>
          </div>
        </div>
        </div>
      </main>

      <SiteFooter />

      {/* ── Info Modal ───────────────────────────────────────────────────── */}
      {activeModal && <InfoModal id={activeModal} onClose={closeModal} />}

      {/* O15: change log modal */}
      {showChanges && (
        <div className="vc-modal-backdrop" onClick={() => setShowChanges(false)}>
          <div className="vc-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="changes-title">
            <div className="vc-modal__head">
              <h2 id="changes-title" className="vc-modal__title">What's changed</h2>
              <button type="button" className="vc-close" onClick={() => setShowChanges(false)} aria-label="Close" autoFocus>✕</button>
            </div>
            <div className="vc-modal__body">
              <p className="vc-small">
                Rate updates and methodology changes that affect what Veercal's calculators show. Newest first.
              </p>
              {CHANGES.map((c, i) => (
                <div key={c.date + '-' + i} className="vc-calc__block" style={{ gap: 6, paddingTop: i ? 14 : 0, borderTop: i ? `1px solid ${tokensV2.line}` : 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                    <h3 className="vc-checks__option">{c.title}</h3>
                    <span className="vc-small" style={{ flexShrink: 0 }}>{c.date}</span>
                  </div>
                  <p>{c.body}</p>
                </div>
              ))}
              <p className="vc-fine">
                Current financial year: <strong>{ONCOSTS_META.financialYear}</strong> · Last reviewed {ONCOSTS_META.lastReviewed} · Next review {ONCOSTS_META.nextReviewDue}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Print Summary — only visible when printing ──────────────────── */}
      <div className="vc-print-only">
        <div className="vc-container" style={{ padding: "24px 0", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", borderBottom: `2px solid ${tokensV2.ink}`, paddingBottom: 12 }}>
            <img src="/brand/v2/veercal-logo.svg" alt="Veercal" width="127" height="30" />
            <span className="vc-small">Car Ownership Cost Calculator · veercal.com</span>
            <span style={{ ...LBL, fontSize: 13, marginLeft: "auto" }}>
              Generated {new Date().toLocaleDateString("en-AU", {day:"2-digit",month:"short",year:"numeric"})} · Rates FY2026–27
            </span>
          </div>

          <div className="vc-aside">
            <p className="vc-aside__text">
              General information only — not financial advice. All figures are indicative estimates. Consult a licensed financial adviser before making financial decisions.
            </p>
          </div>

          <p style={{ margin: 0 }}>
            <strong>Vehicle:</strong> {fmt(d.vehiclePrice)} · <strong>Hold:</strong> {d.holdYears} years · <strong>Annual KM:</strong> {d.annualKm.toLocaleString()} · <strong>State:</strong> {d.state} · <strong>Running/yr:</strong> {fmt(running(d))}
          </p>

          <table className="vc-table vc-table--compact">
            <thead>
              <tr>
                {[
                  { h: "Method",        tip: "The finance structure being compared" },
                  { h: "True Total Cost", tip: "Every dollar in minus exit sale value: purchase + on-costs + finance + running − what you sell for. The single most honest comparison number." },
                  { h: "Eff. Monthly",  tip: "True total cost divided by the number of months in your hold period. Normalises different structures for fair comparison." },
                  { h: "Upfront",       tip: "Cash required on day one: deposit + stamp duty. Does not include ongoing payments." },
                  { h: "Monthly Payment", tip: "Finance repayment only — does not include running costs (fuel, insurance, rego, servicing). Add running costs for true monthly outflow." },
                  { h: "Total Interest", tip: "All interest paid over the loan or lease term. For cash this is $0 (but opportunity cost applies). For novated, tax savings offset this." },
                  { h: "Exit Value",    tip: "Estimated market value of the vehicle at the end of your hold period, based on the depreciation rates you set. Deducted from total cost." },
                ].map(({ h, tip }) => (
                  <th key={h} scope="col">
                    <span title={tip}>{h}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.method}>
                  <th scope="row">{METHOD_META[r.method].label}</th>
                  <td><strong>{fmt(r.totalCost)}</strong></td>
                  <td>{fmt(r.effectiveMonthly)}</td>
                  <td>{fmt(r.upfrontCost)}</td>
                  <td>{r.monthlyPayment ? fmt(r.monthlyPayment) : "—"}</td>
                  <td>{r.totalInterest ? fmt(r.totalInterest) : "$0"}</td>
                  <td>{r.exitValue ? fmt(r.exitValue) : "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="vc-fine vc-fine--rule">
            Veercal · veercal.com · Rates verified {ONCOSTS_META.lastReviewed} · Next review {ONCOSTS_META.nextReviewDue} · {ONCOSTS_META.source}
          </p>
        </div>
      </div>
    </ErrorBoundary>
  );
}
